// HeatmapCanvas — three layers:
//   1) Heatmap bands (liquidation cluster intensity)
//   2) OHLC candles on top
//   3) Overlay: grid, critical/support zones, price axis, current-price line
//
// Self-contained: takes clusters + candles + livePrice as props and draws.
// Handles DPR, ResizeObserver, and incremental redraws via refs.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { computeTrendline } from "./trendlineMath";
import { detectDoubleTopBottom, detectTriangle, detectHeadAndShoulders, detectInverseHeadAndShoulders, detectCandlePatterns } from "./chartPatterns";
import { computeEMA, detectAllEMACrosses } from "./emaMath";
import { tLm } from "../../i18n/liqmap.js";
import * as drawingEngine from "./drawingEngine";
import { pointerToAnchor } from "./chartCoords";

// Gradient stops (re-calibrated 2026-06 per user spec — more space for cold
// colors so only true high-intensity clusters reach warm tones).
const GRADIENT = [
  [0.00, [2, 0, 12]],
  [0.10, [0, 0, 50]],
  [0.25, [0, 30, 90]],
  [0.40, [0, 80, 40]],
  [0.55, [20, 140, 0]],
  [0.68, [180, 180, 0]],
  [0.78, [255, 90, 0]],
  [0.88, [220, 10, 10]],
  [1.00, [255, 240, 60]],
];

// Render-time intensity threshold — buckets below this are skipped entirely
// (left as dark background). Eliminates visual noise from low-OI bands.
const RENDER_INTENSITY_THRESHOLD = 0.15;
// Client-side renormalization percentile — squeezes mid intensities so only
// the top ~15% of buckets reach warm colors. Applied on top of the backend
// p95 normalization without touching the aggregator math.
const CLIENT_RENORM_PERCENTILE = 0.85;

const lerp = (a, b, t) => a + (b - a) * t;
const colorAt = (v) => {
  v = Math.max(0, Math.min(1, v));
  for (let i = 0; i < GRADIENT.length - 1; i++) {
    const [v0, c0] = GRADIENT[i];
    const [v1, c1] = GRADIENT[i + 1];
    if (v <= v1) {
      const t = (v - v0) / (v1 - v0 || 1);
      return [lerp(c0[0], c1[0], t), lerp(c0[1], c1[1], t), lerp(c0[2], c1[2], t)];
    }
  }
  return GRADIENT[GRADIENT.length - 1][1];
};
const rgba = ([r, g, b], a = 1) => `rgba(${r | 0},${g | 0},${b | 0},${a})`;

const fmtPrice = (p) => {
  if (p == null) return "—";
  if (p >= 1000) return `$${p.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (p >= 1) return `$${p.toFixed(2)}`;
  return `$${p.toFixed(4)}`;
};

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

const RIGHT_AXIS = 68;
const BOTTOM_AXIS = 22;

export default function HeatmapCanvas({ clusters = [], candles = [], livePrice = null, stats = {}, highlightPrice = null, simulation = null, whales = null, srData = null, showRange = false, showTrendline = false, showPatterns = false, showCandles = false, showEMA = false, showStochastic = false, stochasticCrosses = [], coin = "BTC", timeframe = "1h", lang = "es", onResetView = null, fullscreenTargetRef = null, showDrawingTool = false, drawings = [], onDrawingsChange = null, activeTool = "cursor", magnetOn = false, allDrawingsLocked = false, allDrawingsHidden = false, persistentMode = false, onAutoRevertTool = null }) {
  const ref = useRef(null);
  const stateRef = useRef({ w: 0, h: 0, dpr: 1, ctx: null });
  const overlayRef = useRef(null);
  const pulseRafRef = useRef(null);
  const crossRef = useRef(null);
  const simRef = useRef(null);
  const simRafRef = useRef(null);
  const whaleRef = useRef(null);
  // EMA overlay canvas — dedicated 8th layer for the 4 EMA lines
  // (9/21/50/200) plus the 3 cross markers. Lives between whaleRef and
  // srRef in z-order: above ballenas, below S/R IA / patterns (analytic
  // signals still win visual prominence over derived indicators).
  const emaRef = useRef(null);
  const srRef = useRef(null);
  // Per-layer "latest redraw" refs — populated by each overlay effect on
  // every data-change run. The single ResizeObserver on `rootRef` reads
  // these refs and invokes them after re-fitting all 7 canvas bitmaps,
  // so a container size change (fullscreen toggle, window resize, mobile
  // rotation) refreshes every overlay without restarting any animation.
  // Skipped intentionally for the overlay-halo + sim-pulse canvases:
  // both run their own RAF loop that self-fits per frame, so the central
  // re-fit is enough.
  const crossRedrawRef = useRef(null);
  const whaleRedrawRef = useRef(null);
  const emaRedrawRef = useRef(null);
  const srRedrawRef = useRef(null);
  const drawCanvasRedrawRef = useRef(null);
  // Coalescer handle for the central ResizeObserver — cancels any pending
  // RAF before scheduling a new one so a continuous drag-resize batches
  // into at most one redraw per frame.
  const resizeRafRef = useRef(null);

  // Range-line hit zones — populated inside the draw effect every time the
  // Range overlay is rendered. Holds { hi: {y, t, price}, lo: {y, t, price} }.
  // Pointer handlers read this ref to know if cursor is near a line.
  const rangeHitRef = useRef(null);
  // Tooltip active state — `null | { kind: 'hi'|'lo', x, y }`.
  const [rangeTip, setRangeTip] = useState(null);
  // Trendline hit data — populated inside the draw effect when a trendline
  // is visible. Holds the line equation, pivot screen coords and metadata
  // so pointer handlers can do hit detection without recomputing math.
  // Shape: { kind, line:{m,b}, xStart, xEnd, points:[{x,y,p,t,i,role}] }
  const trendHitRef = useRef(null);
  // Tooltip active state for trendline — `null | { x, y }` (anchor at cursor).
  const [trendTip, setTrendTip] = useState(null);
  // Patterns (Double Top / Bottom) hit data — populated inside the draw effect
  // when a pattern is rendered. Holds anchor screen coords + neckline geometry
  // + the full result object so pointer handlers can show a rich tooltip
  // without recomputing detection math.
  const patternsHitRef = useRef(null);
  // Tooltip active state for patterns — `null | { x, y }`.
  const [patternTip, setPatternTip] = useState(null);
  // Candle-pattern hit data — array of `{ i, x, y, pattern, signal, metrics, thresholds }`
  // populated inside the draw effect when `showCandles` is on.
  const candlesHitRef = useRef([]);
  // ── Drawing engine state (Fase B1) ────────────────────────────────
  // `drawRef` is the 7th canvas dedicated to manual drawings. It sits on
  // top of the analytic overlays (srRef) and below the DOM tooltips. Its
  // pointerEvents flip between 'auto'/'none' depending on `showDrawingTool`
  // and `activeTool` — only the eraser/draw tools need to swallow input,
  // cursor mode lets pan/zoom through. `draftRef` holds the in-progress
  // drawing between pointerdown and pointerup; `eraserHoverRef` mirrors
  // the pointer so the renderer can paint the red highlight halo.
  const drawRef = useRef(null);
  const draftRef = useRef(null);
  const eraserHoverRef = useRef(null);
  // ── B7a — cursor-mode drag-to-reposition state (ALL drawing types) ─
  // Generalized from the bars_pattern-only B5b path: any non-locked
  // drawing can now be dragged in cursor mode. Stores the SNAPSHOT of
  // the drawing at mousedown (not just an anchor) so each move applies
  // translateDrawing to the original — no cumulative drift.
  // Shape: { id, startDrawing, startPointer:{idx,price} } | null
  const dragRepositionRef = useRef(null);
  // Bumped on every draft mutation to trigger a redraw without storing
  // the (mutable) draft in React state.
  const [draftRev, setDraftRev] = useState(0);
  // ── B3b — Right-click contextual menu state ───────────────────────
  // Stores { x, y, drawingId } where x/y are CLIENT coords (used by the
  // DOM overlay rendered with position: fixed). Null when closed.
  const [contextMenu, setContextMenu] = useState(null);
  // Active candle tooltip — `null | { x, y, hit }` where `hit` is one entry from candlesHitRef.
  const [candleTip, setCandleTip] = useState(null);
  // Pre-compute candle pattern detection ONCE per (candles, coin) so the
  // canvas draw and the DOM mini-legend share the same source of truth.
  // The legend uses the length for its header counter; the draw effect
  // re-uses the result for marker placement and clustering. Empty array
  // when `showCandles` is off keeps the legend hidden via `length > 0`.
  const candlePatterns = useMemo(() => {
    if (!showCandles || !candles.length) return [];
    try { return detectCandlePatterns(candles, coin) || []; }
    catch { return []; }
  }, [showCandles, candles, coin]);
  // Mini-legend collapse state — persisted to localStorage so users keep
  // their preference across reloads. Starts EXPANDED on first paint to
  // surface the new visual vocabulary (✕ / ▼ / ▲ / ◆).
  const [candleLegendCollapsed, setCandleLegendCollapsed] = useState(() => {
    try { return localStorage.getItem("liqmap_candle_legend_collapsed") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("liqmap_candle_legend_collapsed", candleLegendCollapsed ? "1" : "0"); }
    catch { /* ignore quota / private mode */ }
  }, [candleLegendCollapsed]);
  // Hover tooltip — { mouseX, mouseY, bucket, priceAt } | null
  const [hoverBucket, setHoverBucket] = useState(null);
  const longPressTimerRef = useRef(null);
  // Root wrapper ref — anchor for native non-passive wheel listener + fullscreen target.
  const rootRef = useRef(null);
  const onWheelRef = useRef(null); // always points to latest wheel handler closure

  // Zoom & pan state (Y-axis / price only). zoomScale=1 = full coin range.
  const [zoomScale, setZoomScale] = useState(1);
  const [panFrac, setPanFrac] = useState(0); // -1..+1, fraction of halfRange
  // Crosshair pointer position (chart-local pixels) and last hover info.
  const [pointer, setPointer] = useState(null);
  // Fullscreen state (toggled via the 4th zoom-control button & the
  // browser's `fullscreenchange` event for ESC key handling).
  const [isFullscreen, setIsFullscreen] = useState(false);
  const lastMidRef = useRef(null);

  // Build the visible price range from clusters + candles + livePrice.
  const range = useMemo(() => {
    const ps = [];
    clusters.forEach((c) => ps.push(c.price));
    candles.forEach((c) => { ps.push(c.h); ps.push(c.l); });
    if (livePrice) ps.push(livePrice);
    if (!ps.length) return { min: 0, max: 100 };
    let min = Math.min(...ps);
    let max = Math.max(...ps);
    const pad = (max - min) * 0.06 || max * 0.01;
    return { min: min - pad, max: max + pad };
  }, [clusters, candles, livePrice]);

  // Reset zoom whenever the underlying data shifts to a new coin (midpoint
  // jumps by >40%). We can't see the coin prop directly here, so we detect
  // the range mid-shift instead.
  useEffect(() => {
    const mid = (range.min + range.max) / 2;
    const prev = lastMidRef.current;
    if (prev != null && prev > 0 && Math.abs(mid - prev) / prev > 0.4) {
      setZoomScale(1);
      setPanFrac(0);
    }
    lastMidRef.current = mid;
  }, [range.min, range.max]);

  // Effective Y view window after zoom + pan.
  const viewRange = useMemo(() => {
    const center0 = (range.min + range.max) / 2;
    const halfRange = (range.max - range.min) / 2;
    const newHalf = halfRange / Math.max(0.0001, zoomScale);
    const center = center0 + panFrac * halfRange;
    return { min: center - newHalf, max: center + newHalf };
  }, [range.min, range.max, zoomScale, panFrac]);

  // Layered draw — re-runs whenever inputs change.
  const draw = () => {
    const cnv = ref.current;
    if (!cnv) return;
    const ctx = stateRef.current.ctx || cnv.getContext("2d");
    if (!ctx) return;
    const { w, h } = stateRef.current;
    if (!w || !h) return;

    ctx.clearRect(0, 0, w, h);
    const chartW = w - RIGHT_AXIS;
    const chartH = h - BOTTOM_AXIS;
    const yOf = (price) => {
      const min = viewRange.min, max = viewRange.max;
      if (max <= min) return chartH;
      return chartH - ((price - min) / (max - min)) * chartH;
    };

    // ───── Layer 1: Discrete horizontal liquidation lines (temporal-origin) ─────
    // 2026-06-15 — Lines start at a horizontal position derived from their
    // intensity, mimicking CoinGlass: high-intensity buckets (longer-lived
    // positions) start near the left edge and span the full chart, while
    // low-intensity buckets (recent positions) start to the right. All lines
    // terminate near the right edge (current price). Backend math untouched.
    if (clusters.length) {
      // Calibration pass — recompute p85 of incoming intensities and apply a
      // log-shaped visual curve. Backend math is untouched; this is a pure
      // render-side perception fix to make cold zones dominate the canvas.
      const intensitiesAsc = clusters
        .map((c) => Number(c.intensity) || 0)
        .filter((v) => v > 0)
        .sort((a, b) => a - b);
      const p85 = intensitiesAsc.length
        ? intensitiesAsc[Math.min(intensitiesAsc.length - 1, Math.floor(intensitiesAsc.length * CLIENT_RENORM_PERCENTILE))]
        : 1;
      const denom = p85 > 0 ? p85 : 1;
      const LOG10 = Math.log(10);

      // Deterministic per-price jitter — keeps the line origin stable across
      // re-renders (no flicker on zoom/pan/data tick).
      const jitter = (price) => {
        const k = Math.sin(price * 1271.917) * 43758.5453;
        return (k - Math.floor(k)) - 0.5; // [-0.5, +0.5]
      };

      const xEnd = chartW * 0.98;
      const drawList = [];
      for (const c of clusters) {
        const renorm = Math.min(1, (Number(c.intensity) || 0) / denom);
        // log10(1 + 9x): compresses highs, expands lows so weak buckets stay dim.
        const vis = Math.log(1 + renorm * 9) / LOG10;
        if (vis < RENDER_INTENSITY_THRESHOLD) continue;
        drawList.push({ price: c.price, vis });
      }
      // Sort ASC so the most intense lines paint LAST (on top).
      drawList.sort((a, b) => a.vis - b.vis);

      ctx.lineCap = "round";
      for (const d of drawList) {
        // Temporal origin — higher intensity ⇒ starts further left.
        let xStart = chartW * (1 - d.vis) * 0.70 + jitter(d.price) * chartW * 0.03;
        xStart = Math.max(0, Math.min(xEnd - 4, xStart));
        const y = yOf(d.price);
        const lineH = Math.max(1, d.vis * 5);
        const alpha = 0.25 + d.vis * 0.75;
        const col = colorAt(d.vis);
        const [r, g, b] = [col[0] | 0, col[1] | 0, col[2] | 0];

        // Horizontal soft-edge gradient.
        const grad = ctx.createLinearGradient(xStart, 0, xEnd, 0);
        grad.addColorStop(0, `rgba(${r},${g},${b},0)`);
        grad.addColorStop(0.05, `rgba(${r},${g},${b},${alpha})`);
        grad.addColorStop(0.95, `rgba(${r},${g},${b},${alpha})`);
        grad.addColorStop(1, `rgba(${r},${g},${b},${alpha * 0.6})`);

        ctx.strokeStyle = grad;
        ctx.lineWidth = lineH;
        ctx.beginPath();
        ctx.moveTo(xStart, y);
        ctx.lineTo(xEnd, y);
        ctx.stroke();

        // Cluster of secondary lines for dense buckets — gives the CoinGlass
        // "stacked threads" effect on hot zones.
        if (d.vis > 0.6) {
          const sideAlpha = alpha * 0.4;
          const sideGrad = ctx.createLinearGradient(xStart, 0, xEnd, 0);
          sideGrad.addColorStop(0, `rgba(${r},${g},${b},0)`);
          sideGrad.addColorStop(0.05, `rgba(${r},${g},${b},${sideAlpha})`);
          sideGrad.addColorStop(0.95, `rgba(${r},${g},${b},${sideAlpha})`);
          sideGrad.addColorStop(1, `rgba(${r},${g},${b},${sideAlpha * 0.6})`);
          ctx.strokeStyle = sideGrad;
          ctx.lineWidth = Math.max(1, lineH * 0.5);
          for (const dy of [-2, 2]) {
            ctx.beginPath();
            ctx.moveTo(xStart, y + dy);
            ctx.lineTo(xEnd, y + dy);
            ctx.stroke();
          }
        }

        // Extreme cluster highlight (intensity > 0.85): brighter overlay.
        if (d.vis > 0.85) {
          const hiGrad = ctx.createLinearGradient(xStart, 0, xEnd, 0);
          hiGrad.addColorStop(0, `rgba(255,255,255,0)`);
          hiGrad.addColorStop(0.5, `rgba(255,255,255,${Math.min(0.35, alpha * 0.35)})`);
          hiGrad.addColorStop(1, `rgba(255,255,255,0)`);
          ctx.strokeStyle = hiGrad;
          ctx.lineWidth = lineH + 1;
          ctx.beginPath();
          ctx.moveTo(xStart, y);
          ctx.lineTo(xEnd, y);
          ctx.stroke();
        }
      }
    }

    // ───── Layer 3a: Grid + zones (drawn under candles for readability) ─────
    ctx.strokeStyle = "rgba(255,255,255,0.04)";
    ctx.lineWidth = 1;
    const gridStep = Math.max(stats?.bucket || 100, Math.round(((viewRange.max - viewRange.min) / 6) / (stats?.bucket || 100)) * (stats?.bucket || 100));
    for (let p = Math.ceil(viewRange.min / gridStep) * gridStep; p <= viewRange.max; p += gridStep) {
      const y = yOf(p);
      ctx.beginPath();
      ctx.moveTo(0, y); ctx.lineTo(chartW, y);
      ctx.stroke();
    }

    if (stats?.cluster_max_price != null) {
      const y = yOf(stats.cluster_max_price);
      ctx.fillStyle = "rgba(255,59,92,0.07)";
      ctx.fillRect(0, y - 8, chartW, 16);
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = "rgba(255,59,92,0.45)";
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(chartW, y); ctx.stroke();
      ctx.setLineDash([]);
      roundRect(ctx, 8, y - 9, 92, 14, 4);
      ctx.fillStyle = "#ff3b5c"; ctx.fill();
      ctx.fillStyle = "#fff"; ctx.font = "bold 8px ui-monospace,monospace";
      ctx.fillText("MAX " + fmtPrice(stats.cluster_max_price), 12, y + 1);
    }
    if (stats?.cluster_second_price != null) {
      const y = yOf(stats.cluster_second_price);
      ctx.fillStyle = "rgba(249,115,22,0.06)";
      ctx.fillRect(0, y - 6, chartW, 12);
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = "rgba(249,115,22,0.45)";
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(chartW, y); ctx.stroke();
      ctx.setLineDash([]);
      roundRect(ctx, 8, y - 9, 92, 14, 4);
      ctx.fillStyle = "#f97316"; ctx.fill();
      ctx.fillStyle = "#000"; ctx.font = "bold 8px ui-monospace,monospace";
      ctx.fillText("SUP " + fmtPrice(stats.cluster_second_price), 12, y + 1);
    }

    // ───── Layer 2: Candles ─────
    if (candles.length) {
      const visW = chartW;
      const cw = Math.max(1.5, (visW / candles.length) * 0.7);
      const step = visW / candles.length;
      // Delegate single-candle rendering to drawingEngine.drawCandle so
      // bars_pattern (B5b) can reuse the EXACT same primitive — single
      // source of truth, no visual drift.
      candles.forEach((c, i) => {
        const cx = step * (i + 0.5);
        drawingEngine.drawCandle(ctx, cx, c, yOf, cw);
      });
    }

    // ───── Layer 3b: Current price line + badge ─────
    if (livePrice != null) {
      const y = yOf(livePrice);
      ctx.setLineDash([5, 4]);
      ctx.strokeStyle = "rgba(249,115,22,0.7)";
      ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(chartW, y); ctx.stroke();
      ctx.setLineDash([]);
      // badge
      roundRect(ctx, chartW + 4, y - 9, RIGHT_AXIS - 8, 18, 4);
      ctx.fillStyle = "#f97316"; ctx.fill();
      ctx.fillStyle = "#000"; ctx.font = "bold 10px ui-monospace,monospace";
      const label = fmtPrice(livePrice);
      ctx.fillText(label, chartW + 8, y + 4);
    }

    // ───── Right axis price ticks ─────
    ctx.fillStyle = "#888"; ctx.font = "9px ui-monospace,monospace";
    for (let p = Math.ceil(viewRange.min / gridStep) * gridStep; p <= viewRange.max; p += gridStep) {
      const y = yOf(p);
      ctx.fillText(fmtPrice(p), chartW + 6, y + 3);
    }

    // ───── Bottom axis ─────
    ctx.fillStyle = "#2a2a2a";
    ctx.fillRect(0, chartH, w, BOTTOM_AXIS);
    ctx.fillStyle = "#666"; ctx.font = "9px ui-monospace,monospace";
    if (candles.length) {
      const first = candles[0];
      const last = candles[candles.length - 1];
      const fmt = (t) => {
        const d = new Date(t);
        return d.toLocaleString([], { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
      };
      ctx.fillText(fmt(first.t), 6, chartH + 14);
      const lastW = ctx.measureText(fmt(last.t)).width;
      ctx.fillText(fmt(last.t), chartW - lastW - 6, chartH + 14);
    }
  };

  // ─── Single ResizeObserver on the heatmap root ─────────────────────
  // Observes `rootRef` (parent of all 7 canvases) so a container size
  // change from ANY cause — fullscreen toggle, window resize, mobile
  // orientation change, sidebar collapse, etc. — refits every canvas
  // bitmap and triggers each layer's redraw. Previously only the main
  // heatmap canvas had an observer, leaving the 5 overlays stuck on
  // their stale bitmap dimensions (causing the "fullscreen at half
  // screen" bug). Coalesced with requestAnimationFrame so a continuous
  // drag-resize batches into at most one fit/redraw per frame.
  useEffect(() => {
    const host = rootRef.current;
    if (!host) return undefined;
    // Cache the main canvas 2D context up-front (mirrors the original
    // observer's stateRef.ctx caching). Per-canvas effects also call
    // getContext on demand — the cache is purely an optimization for
    // the main `draw()` hot path.
    const mainCnv = ref.current;
    if (mainCnv) stateRef.current.ctx = mainCnv.getContext("2d");

    const fitAllCanvases = (rect) => {
      const dpr = window.devicePixelRatio || 1;
      stateRef.current.dpr = dpr;
      stateRef.current.w = rect.width;
      stateRef.current.h = rect.height;
      // Fit bitmap of every canvas to the container rect at the current
      // DPR, then reset the 2D transform. All 7 canvases share the same
      // CSS box (absolute inset-0 h-full w-full) so a single rect is
      // authoritative.
      const allRefs = [ref, overlayRef, crossRef, simRef, whaleRef, emaRef, srRef, drawRef];
      for (const r of allRefs) {
        const cnv = r.current;
        if (!cnv) continue;
        cnv.width = rect.width * dpr;
        cnv.height = rect.height * dpr;
        const ctx = cnv.getContext("2d");
        if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      }
      // Invoke the redraw closure each layer published the last time
      // its data-driven effect ran. This is the SAME function the
      // effect would have called on a data change — we're just adding
      // size as an additional trigger, not duplicating draw logic.
      // Main canvas: `draw()` is already at component scope.
      draw();
      crossRedrawRef.current?.();
      whaleRedrawRef.current?.();
      emaRedrawRef.current?.();
      srRedrawRef.current?.();
      drawCanvasRedrawRef.current?.();
      // overlay-halo + sim-pulse canvases each own a RAF loop that
      // self-fits on every frame, so a size change is picked up on
      // their next tick without an explicit redraw call here.
    };

    const ro = new ResizeObserver((entries) => {
      if (!entries.length) return;
      const rect = entries[0].contentRect;
      if (!rect.width || !rect.height) return;
      // Coalesce: any pending RAF from an earlier tick is cancelled so
      // continuous drag-resize batches into one fit/redraw per frame.
      if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current);
      resizeRafRef.current = requestAnimationFrame(() => {
        resizeRafRef.current = null;
        fitAllCanvases(rect);
      });
    });
    ro.observe(host);
    return () => {
      if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current);
      ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redraw on data changes.
  useEffect(() => { draw(); /* eslint-disable-next-line */ }, [clusters, candles, livePrice, viewRange.min, viewRange.max, stats?.cluster_max_price, stats?.cluster_second_price]);

  // Pulsing halo for the clicked cluster price — runs on its own canvas
  // over the main heatmap, fades out after ~3.5s.
  useEffect(() => {
    const ov = overlayRef.current;
    if (!ov || highlightPrice == null) return undefined;
    // highlightPrice can be a number, or "price|ts" string to force re-fire.
    const parsed = typeof highlightPrice === "string"
      ? parseFloat(highlightPrice.split("|")[0])
      : Number(highlightPrice);
    if (!isFinite(parsed)) return undefined;
    const ctx = ov.getContext("2d");
    if (!ctx) return undefined;
    const dpr = window.devicePixelRatio || 1;
    const fit = () => {
      const r = ov.getBoundingClientRect();
      ov.width = r.width * dpr; ov.height = r.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return r;
    };
    const r = fit();
    const chartH = r.height - 22;
    const chartW = r.width - 68;
    const yOf = (p) => {
      if (viewRange.max <= viewRange.min) return chartH;
      return chartH - ((p - viewRange.min) / (viewRange.max - viewRange.min)) * chartH;
    };
    const start = performance.now();
    const DURATION = 3500;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / DURATION);
      ctx.clearRect(0, 0, r.width, r.height);
      const y = yOf(parsed);
      // pulsating ring (3 cycles over duration)
      const pulse = (Math.sin((t * 3 + 0) * Math.PI * 2) + 1) / 2;
      const alpha = (1 - t) * (0.45 + 0.45 * pulse);
      ctx.fillStyle = `rgba(168, 85, 247, ${alpha * 0.18})`;
      ctx.fillRect(0, y - 18, chartW, 36);
      ctx.setLineDash([6, 6]);
      ctx.strokeStyle = `rgba(168, 85, 247, ${alpha})`;
      ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(chartW, y); ctx.stroke();
      ctx.setLineDash([]);
      // arrow + label
      ctx.fillStyle = `rgba(168, 85, 247, ${alpha})`;
      ctx.beginPath();
      ctx.moveTo(chartW - 18, y - 8);
      ctx.lineTo(chartW - 4, y);
      ctx.lineTo(chartW - 18, y + 8);
      ctx.closePath();
      ctx.fill();
      if (t < 1) {
        pulseRafRef.current = requestAnimationFrame(tick);
      } else {
        ctx.clearRect(0, 0, r.width, r.height);
      }
    };
    pulseRafRef.current = requestAnimationFrame(tick);
    return () => { if (pulseRafRef.current) cancelAnimationFrame(pulseRafRef.current); };
  }, [highlightPrice, viewRange.min, viewRange.max]);

  // ───────── Interaction: zoom (wheel + pinch + buttons) + pan + crosshair ─────────
  const zoomBy = (factor, anchorFrac = 0) => {
    // anchorFrac in [-1..+1] where 0 = canvas vertical center, -1 = top, +1 = bottom.
    // Pre-zoom price under anchor:
    const halfRange = (range.max - range.min) / 2;
    const center0 = (range.min + range.max) / 2;
    const oldHalf = halfRange / Math.max(0.0001, zoomScale);
    const priceUnderAnchor = (center0 + panFrac * halfRange) + anchorFrac * oldHalf;
    const nextScale = Math.min(5, Math.max(1, zoomScale * factor));
    const newHalf = halfRange / nextScale;
    // Choose new center so the priceUnderAnchor stays under the same anchor pixel.
    const newCenter = priceUnderAnchor - anchorFrac * newHalf;
    const newPanFrac = Math.max(-0.85, Math.min(0.85, (newCenter - center0) / (halfRange || 1)));
    setZoomScale(nextScale);
    setPanFrac(newPanFrac);
  };
  const resetZoom = () => {
    setZoomScale(1);
    setPanFrac(0);
    // Also clears the parent's time-range brush so a single click on ↺
    // restores BOTH axes (Y zoom/pan + X brush) — coordinated reset per
    // 2026-02-17 product decision.
    if (typeof onResetView === "function") onResetView();
  };

  // Helpers — pointer / touch
  const dragRef = useRef(null);
  const pinchRef = useRef(null);

  const onWheel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const host = rootRef.current || e.currentTarget;
    const rect = host.getBoundingClientRect();
    const chartH = rect.height - BOTTOM_AXIS;
    const yLocal = e.clientY - rect.top;
    const anchor = ((yLocal / chartH) - 0.5) * 2; // -1..+1
    const factor = e.deltaY < 0 ? 1.18 : 1 / 1.18;
    zoomBy(factor, anchor);
  };
  // Keep ref pointing at the latest closure so the native listener (attached
  // once below) always reads fresh zoom state.
  onWheelRef.current = onWheel;

  // Attach a NATIVE non-passive wheel listener on the root so we can call
  // preventDefault() and stop the page from scrolling while zooming. React's
  // synthetic onWheel is passive by default (React 17+), which is why the
  // page used to scroll on every wheel tick. { passive: false } is mandatory.
  useEffect(() => {
    const host = rootRef.current;
    if (!host) return undefined;
    const handler = (e) => { if (onWheelRef.current) onWheelRef.current(e); };
    host.addEventListener("wheel", handler, { passive: false });
    return () => host.removeEventListener("wheel", handler);
  }, []);

  // ─── Fullscreen control (4th button in the zoom group) ──────────────────
  const handleFullscreen = () => {
    // Prefer an external fullscreen target (set by the parent so the chart
    // container — toolbar + canvas — goes fullscreen together). Fall back
    // to the internal rootRef when no external target is provided.
    const host = (fullscreenTargetRef && fullscreenTargetRef.current) || rootRef.current;
    if (!host) return;
    const docFs = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement;
    if (!docFs) {
      const req = host.requestFullscreen
        || host.webkitRequestFullscreen
        || host.mozRequestFullScreen;
      if (req) {
        try {
          const p = req.call(host);
          if (p && typeof p.catch === "function") {
            p.catch((err) => { /* eslint-disable-next-line no-console */ console.log("Fullscreen error:", err); });
          }
        } catch (err) { /* eslint-disable-next-line no-console */ console.log("Fullscreen error:", err); }
      }
    } else {
      const exit = document.exitFullscreen
        || document.webkitExitFullscreen
        || document.mozCancelFullScreen;
      if (exit) try { exit.call(document); } catch { /* noop */ }
    }
  };

  useEffect(() => {
    const onFsChange = () => {
      const el = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement;
      setIsFullscreen(!!el);
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    document.addEventListener("mozfullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
      document.removeEventListener("mozfullscreenchange", onFsChange);
    };
  }, []);

  // ─── Drawing engine — pointer routing helper ──────────────────────────
  // Returns true when the engine consumed the event (caller must short-
  // circuit pan/zoom). `kind` ∈ {'down','move','up'}; coordinates are read
  // from the React synthetic event for both mouse and touch via `getXY`.
  const getXY = (e) => {
    if (e.touches && e.touches[0]) return { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
    if (e.changedTouches && e.changedTouches[0]) return { clientX: e.changedTouches[0].clientX, clientY: e.changedTouches[0].clientY };
    return { clientX: e.clientX, clientY: e.clientY };
  };
  const tryDrawingPointer = (e, kind) => {
    // ── B7a — cursor-mode drag-to-reposition for ALL drawing types ──
    // Runs BEFORE shouldInterceptPointer so we can hijack mouse events
    // in cursor mode (which otherwise pass through to pan). Hijack only
    // when the pointer hits a non-locked drawing's hit-region. Generalized
    // from the bars_pattern-only B5b: drag applies translateDrawing on a
    // snapshot of the drawing taken at mousedown — no drift across moves.
    if (showDrawingTool && activeTool === "cursor") {
      const rect = e.currentTarget.getBoundingClientRect();
      const { clientX, clientY } = getXY(e);
      const px = clientX - rect.left;
      const py = clientY - rect.top;
      const chartW = rect.width - RIGHT_AXIS;
      const chartH = rect.height - BOTTOM_AXIS;
      const nCandles = candles.length || 1;
      if (px >= 0 && px <= chartW && py >= 0 && py <= chartH) {
        const ctxCoords = { viewRange, chartW, chartH, nCandles };
        if (kind === "down") {
          // Drag is LEFT-click only. Right-click belongs to the context
          // menu (B3b) and must not start a drag. e.button is undefined
          // for touch events (treat as 0 = primary).
          const button = typeof e.button === "number" ? e.button : 0;
          if (button !== 0) return false;
          const hit = drawingEngine.hitTest(drawings, px, py, ctxCoords);
          if (hit && !hit.locked) {
            const ptrAnchor = pointerToAnchor(px, py, ctxCoords);
            dragRepositionRef.current = {
              id: hit.id,
              startDrawing: hit,
              startPointer: { idx: ptrAnchor.idx, price: ptrAnchor.price },
            };
            // B7c (cursor visual): grabbing while drag is active.
            try { e.currentTarget.style.cursor = "grabbing"; } catch { /* noop */ }
            return true; // swallow → pan/zoom does not fire
          }
        } else if (dragRepositionRef.current && (kind === "move" || kind === "up")) {
          const ptrAnchor = pointerToAnchor(px, py, ctxCoords);
          const repos = dragRepositionRef.current;
          const di = ptrAnchor.idx - repos.startPointer.idx;
          const dp = ptrAnchor.price - repos.startPointer.price;
          // Magnet (idx-rounding) ONLY fires on release — keeps the
          // move silky and matches the bars_pattern precedent.
          const roundIdx = kind === "up" && magnetOn;
          const translated = drawingEngine.translateDrawing(
            repos.startDrawing, di, dp, roundIdx,
          );
          if (onDrawingsChange) {
            onDrawingsChange(drawings.map((d) =>
              d.id === repos.id ? translated : d,
            ));
          }
          if (kind === "up") {
            dragRepositionRef.current = null;
            // After release, fall through to the hover branch below
            // (same pointer position, no active drag) so the cursor
            // settles into 'grab' if still over the drawing, or default.
            try {
              const hover = drawingEngine.hitTest(drawings, px, py, ctxCoords);
              e.currentTarget.style.cursor =
                hover && !hover.locked ? "grab" : "";
            } catch { /* noop */ }
          }
          return true;
        } else if (kind === "move") {
          // B7c — hover affordance: while NO drag is in progress, cursor
          // becomes 'grab' over any non-locked drawing under the pointer.
          // This is the discoverability cue for the B7a drag gesture.
          try {
            const hover = drawingEngine.hitTest(drawings, px, py, ctxCoords);
            e.currentTarget.style.cursor =
              hover && !hover.locked ? "grab" : "";
          } catch { /* noop */ }
        }
      }
    }

    if (!drawingEngine.shouldInterceptPointer(showDrawingTool, activeTool)) return false;
    const rect = e.currentTarget.getBoundingClientRect();
    const { clientX, clientY } = getXY(e);
    const px = clientX - rect.left;
    const py = clientY - rect.top;
    const chartW = rect.width - RIGHT_AXIS;
    const chartH = rect.height - BOTTOM_AXIS;
    const nCandles = candles.length || 1;
    // Outside the chart area? ignore.
    if (px < 0 || px > chartW || py < 0 || py > chartH) {
      // Still cancel any draft if pointer leaves the chart.
      if (kind === "up") { draftRef.current = null; setDraftRev((v) => v + 1); }
      return false;
    }
    const ctxCoords = { viewRange, chartW, chartH, nCandles };
    let anchor = pointerToAnchor(px, py, ctxCoords);
    anchor = drawingEngine.snapAnchor(anchor, magnetOn, candles, ctxCoords);

    if (kind === "down") {
      // ── B5b — bars_pattern: place-phase commit ─────────────────
      // If we already captured ratios in a previous gesture, the
      // pointerdown is treated as the placement click — commit a
      // permanent drawing and tear down the draft.
      if (
        activeTool === drawingEngine.TOOLS.BARS_PATTERN &&
        draftRef.current &&
        draftRef.current.type === "bars_pattern" &&
        draftRef.current.phase === "place"
      ) {
        const final = {
          type: "bars_pattern",
          id: draftRef.current.id,
          ratios: draftRef.current.ratios,
          span_idx: draftRef.current.span_idx,
          anchor: { idx: anchor.idx, price: anchor.price },
          color: draftRef.current.color,
          width: draftRef.current.width,
          opacity: draftRef.current.opacity,
          fillAlpha: draftRef.current.fillAlpha,
        };
        draftRef.current = null;
        setDraftRev((v) => v + 1);
        if (onDrawingsChange) {
          onDrawingsChange([...drawings, final]);
          if (!persistentMode && onAutoRevertTool) onAutoRevertTool();
        }
        return true;
      }
      if (drawingEngine.isEraserTool(activeTool)) {
        // Global lock blocks all eraser mutations (B3a).
        if (allDrawingsLocked) return true;
        const hit = drawingEngine.hitTest(drawings, px, py, ctxCoords);
        // Per-drawing lock (B3b): the eraser silently skips locked items.
        if (hit && !hit.locked && onDrawingsChange) {
          onDrawingsChange(drawings.filter((d) => d.id !== hit.id));
        }
        return true;
      }
      if (drawingEngine.isMultiStepTool(activeTool)) {
        // Channel & position — each pointerdown commits one anchor.
        const { draft, finalized } = drawingEngine.advanceMultiStep(activeTool, draftRef.current, anchor, nCandles);
        draftRef.current = draft;
        setDraftRev((v) => v + 1);
        if (finalized && onDrawingsChange) {
          onDrawingsChange([...drawings, finalized]);
          if (!persistentMode && onAutoRevertTool) onAutoRevertTool();
        }
        return true;
      }
      if (drawingEngine.isClickTool(activeTool)) {
        // Single-click finalize. For text we prompt for the string.
        let draft = drawingEngine.startDraft(activeTool, anchor);
        if (draft && draft.type === "text") {
          // eslint-disable-next-line no-alert
          const promptMsg = activeTool === "flag"
            ? (tLm(lang).patterns?.drawing?.prompt_flag || "Etiqueta del flag:")
            : (tLm(lang).patterns?.drawing?.prompt_text || "Texto:");
          const txt = (typeof window !== "undefined" ? window.prompt(promptMsg, "") : "") || "";
          if (!txt.trim()) return true;
          draft = { ...draft, text: txt.trim() };
        }
        const fin = drawingEngine.finalizeDraft(draft, nCandles);
        if (fin && onDrawingsChange) {
          onDrawingsChange([...drawings, fin]);
          if (!persistentMode && onAutoRevertTool) onAutoRevertTool();
        }
        return true;
      }
      if (drawingEngine.isDragTool(activeTool)) {
        draftRef.current = drawingEngine.startDraft(activeTool, anchor);
        setDraftRev((v) => v + 1);
        return true;
      }
      return true; // unknown tool — still swallow to avoid pan
    }
    if (kind === "move") {
      if (drawingEngine.isEraserTool(activeTool)) {
        eraserHoverRef.current = { x: px, y: py };
        setDraftRev((v) => v + 1); // trigger redraw of hover halo
        return true;
      }
      // Freehand sampling: append a point only when the pointer has
      // moved ≥3 pixels since the last sample. Caps at MAX (defended
      // also by drawingEngine.appendFreehandPoint).
      if (draftRef.current && draftRef.current.type === "freehand") {
        const pts = draftRef.current.points;
        const last = pts[pts.length - 1];
        const lastPx = drawingEngine.pointerToAnchor
          ? null  // we only need the projection function; use anchorToPixel via chartCoords
          : null;
        // Compute distance in pixels via chartCoords helpers.
        const lastIdx = last.idx, lastPr = last.price;
        const lastX = (lastIdx * chartW) / Math.max(1, nCandles) + (chartW / Math.max(1, nCandles)) / 2;
        const lastY = (1 - (lastPr - viewRange.min) / Math.max(1e-9, viewRange.max - viewRange.min)) * chartH;
        const d = Math.hypot(px - lastX, py - lastY);
        if (d >= 3) {
          draftRef.current = drawingEngine.appendFreehandPoint(draftRef.current, anchor);
          setDraftRev((v) => v + 1);
        }
        return true;
      }
      if (draftRef.current && drawingEngine.isDragTool(activeTool)) {
        draftRef.current = drawingEngine.extendDraft(draftRef.current, anchor);
        setDraftRev((v) => v + 1);
        return true;
      }
      if (draftRef.current && drawingEngine.isMultiStepTool(activeTool)) {
        // Preview the next anchor between clicks (live wireframe).
        draftRef.current = drawingEngine.extendDraft(draftRef.current, anchor);
        setDraftRev((v) => v + 1);
        return true;
      }
      return true;
    }
    if (kind === "up") {
      // ── B5b — bars_pattern: capture-phase complete → transition to
      // place phase. Do NOT finalize; keep the draft alive so the next
      // pointerdown commits a placement at the user's click.
      if (
        draftRef.current &&
        draftRef.current.type === "bars_pattern" &&
        draftRef.current.phase === "capture"
      ) {
        const startIdx = draftRef.current.a?.idx;
        const endIdx = draftRef.current.b?.idx;
        const captured = drawingEngine.captureBarsRatios(candles, startIdx, endIdx);
        if (captured) {
          draftRef.current = {
            ...draftRef.current,
            phase: "place",
            ratios: captured.ratios,
            span_idx: captured.span_idx,
            // We DELIBERATELY drop a/b once captured; the placement
            // click defines the new anchor.
            a: null,
            b: null,
          };
          setDraftRev((v) => v + 1);
        } else {
          // Degenerate selection (single candle or out of range) → cancel.
          draftRef.current = null;
          setDraftRev((v) => v + 1);
        }
        return true;
      }
      if (draftRef.current && drawingEngine.isDragTool(activeTool)) {
        const fin = drawingEngine.finalizeDraft(draftRef.current, nCandles);
        // CALLOUT needs a prompt at the end (label text).
        if (fin && fin.type === "callout") {
          // eslint-disable-next-line no-alert
          const txt = (typeof window !== "undefined" ? window.prompt(tLm(lang).patterns?.drawing?.prompt_callout || "Texto del callout:", "") : "") || "";
          draftRef.current = null;
          setDraftRev((v) => v + 1);
          if (txt.trim() && onDrawingsChange) {
            onDrawingsChange([...drawings, { ...fin, text: txt.trim() }]);
            if (!persistentMode && onAutoRevertTool) onAutoRevertTool();
          }
          return true;
        }
        draftRef.current = null;
        setDraftRev((v) => v + 1);
        if (fin && onDrawingsChange) {
          onDrawingsChange([...drawings, fin]);
          if (!persistentMode && onAutoRevertTool) onAutoRevertTool();
        }
      }
      // Multi-step tools (channel/position/triangle/polyline) do NOT
      // finalize on pointerup — they wait for the next click / Enter.
      return true;
    }
    return false;
  };

  const onMouseDown = (e) => {
    if (tryDrawingPointer(e, "down")) return;
    dragRef.current = { y: e.clientY, panFracStart: panFrac, rect: e.currentTarget.getBoundingClientRect() };
  };
  // ─── Bucket-under-cursor lookup (for the detailed hover tooltip) ───
  const findNearestBucket = (priceUnder) => {
    if (!clusters.length || !(priceUnder > 0)) return null;
    const threshold = priceUnder * 0.005; // 0.5%
    let best = null;
    let bestDist = Infinity;
    for (const c of clusters) {
      const d = Math.abs(c.price - priceUnder);
      if (d < bestDist) { bestDist = d; best = c; }
    }
    if (best && bestDist <= threshold && Number(best.intensity) >= 0.1) return best;
    return null;
  };
  const onMouseMove = (e) => {
    if (tryDrawingPointer(e, "move")) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setPointer({ x, y, w: rect.width, h: rect.height });
    // Range tooltip hit detection — show tooltip when cursor is within
    // ±6px of either the MIN or MAX horizontal line.
    const hit = rangeHitRef.current;
    if (hit && x >= 0 && x <= rect.width - RIGHT_AXIS) {
      const HIT = 6;
      if (Math.abs(y - hit.hi.y) <= HIT) {
        setRangeTip({ kind: "hi", x, y, meta: hit.hi });
      } else if (Math.abs(y - hit.lo.y) <= HIT) {
        setRangeTip({ kind: "lo", x, y, meta: hit.lo });
      } else if (rangeTip) {
        setRangeTip(null);
      }
    } else if (rangeTip) {
      setRangeTip(null);
    }
    // Trendline tooltip hit detection — show tooltip when cursor is within
    // ±6px of the line OR within 10px of any pivot dot (more generous on
    // dots so they remain easy to discover on desktop).
    const tHit = trendHitRef.current;
    if (tHit && x >= 0 && x <= rect.width - RIGHT_AXIS) {
      let onTrend = false;
      // 1) Pivot dot hit — 10px Euclidean radius.
      for (const pt of tHit.points) {
        if (Math.hypot(x - pt.x, y - pt.y) <= 10) { onTrend = true; break; }
      }
      // 2) Line hit — vertical distance ≤6px within drawn segment.
      if (!onTrend && x >= tHit.xStart - 4 && x <= tHit.xEnd + 4) {
        const n = candles.length || 1;
        const step = (rect.width - RIGHT_AXIS) / n;
        const idxAtX = (x - step / 2) / step;
        const priceOnLine = tHit.line.m * idxAtX + tHit.line.b;
        const chartH0 = rect.height - BOTTOM_AXIS;
        const min = viewRange.min, max = viewRange.max;
        const yOnLine = chartH0 - ((priceOnLine - min) / (max - min || 1)) * chartH0;
        if (Math.abs(y - yOnLine) <= 6) onTrend = true;
      }
      if (onTrend) {
        setTrendTip({ x, y });
      } else if (trendTip) {
        setTrendTip(null);
      }
    } else if (trendTip) {
      setTrendTip(null);
    }
    // Patterns tooltip hit detection — show tooltip when cursor is within
    // ±10px Euclidean of any anchor dot, OR ±6px vertical of the neckline
    // segment, OR ±10px of the break X marker (when confirmed).
    const pHit = patternsHitRef.current;
    if (pHit && x >= 0 && x <= rect.width - RIGHT_AXIS) {
      let onPattern = false;
      for (const pt of pHit.points) {
        if (Math.hypot(x - pt.x, y - pt.y) <= 10) { onPattern = true; break; }
      }
      // Double Top/Bottom — neckline horizontal segment hit.
      if (!onPattern && pHit.neckSeg
          && x >= pHit.neckSeg.x1 - 4 && x <= pHit.neckSeg.x2 + 4
          && Math.abs(y - pHit.neckSeg.y) <= 5) {
        onPattern = true;
      }
      // Triangle — check both diagonal lines (translate world idx → screen).
      if (!onPattern && pHit.isTriangle) {
        const n = candles.length || 1;
        const step = (rect.width - RIGHT_AXIS) / n;
        const idxAtX = (x - step / 2) / step;
        const chartH0 = rect.height - BOTTOM_AXIS;
        const min = viewRange.min, max = viewRange.max;
        const projY = (line) => {
          const price = line.m * idxAtX + line.b;
          return chartH0 - ((price - min) / (max - min || 1)) * chartH0;
        };
        for (const seg of [pHit.highLineSeg, pHit.lowLineSeg]) {
          if (x >= seg.x1 - 4 && x <= seg.x2 + 4 && Math.abs(y - projY(seg)) <= 6) {
            onPattern = true; break;
          }
        }
      }
      // HCH / Inv HCH — diagonal neckline hit (line equation in world idx).
      if (!onPattern && pHit.isHCH && pHit.necklineSeg) {
        const seg = pHit.necklineSeg;
        if (x >= seg.x1 - 4 && x <= seg.x2 + 4) {
          const n = candles.length || 1;
          const step = (rect.width - RIGHT_AXIS) / n;
          const idxAtX = (x - step / 2) / step;
          const chartH0 = rect.height - BOTTOM_AXIS;
          const min = viewRange.min, max = viewRange.max;
          const price = seg.m * idxAtX + seg.b;
          const yOnLine = chartH0 - ((price - min) / (max - min || 1)) * chartH0;
          if (Math.abs(y - yOnLine) <= 6) onPattern = true;
        }
      }
      if (onPattern) {
        setPatternTip({ x, y });
      } else if (patternTip) {
        setPatternTip(null);
      }
    } else if (patternTip) {
      setPatternTip(null);
    }
    // Candle-pattern tooltip hit detection — pick the closest marker
    // within 10 px Euclidean. We don't show multiple at once; one
    // tooltip at a time is the established UX rule on this canvas.
    const cHits = candlesHitRef.current;
    if (cHits && cHits.length && x >= 0 && x <= rect.width - RIGHT_AXIS) {
      let best = null, bestD = 11;
      for (const h of cHits) {
        const d = Math.hypot(x - h.x, y - h.y);
        if (d <= bestD) { best = h; bestD = d; }
      }
      if (best) {
        setCandleTip({ x, y, hit: best });
      } else if (candleTip) {
        setCandleTip(null);
      }
    } else if (candleTip) {
      setCandleTip(null);
    }
    // Compute price under cursor and lookup nearest bucket.
    const chartH = rect.height - BOTTOM_AXIS;
    const chartW = rect.width - RIGHT_AXIS;
    if (y >= 0 && y <= chartH && x >= 0 && x <= chartW) {
      const priceAt = viewRange.max - (y / chartH) * (viewRange.max - viewRange.min);
      const bucket = findNearestBucket(priceAt);
      if (bucket) {
        setHoverBucket({ mouseX: x, mouseY: y, bucket, priceAt, canvasW: rect.width, canvasH: rect.height });
      } else {
        setHoverBucket(null);
      }
    } else {
      setHoverBucket(null);
    }
    if (dragRef.current) {
      const dy = e.clientY - dragRef.current.y;
      const halfRange = (range.max - range.min) / 2 || 1;
      const newHalf = halfRange / Math.max(0.0001, zoomScale);
      const dPriceFracOfHalf = (dy / (rect.height - BOTTOM_AXIS)) * (newHalf / halfRange) * 2;
      const next = Math.max(-0.85, Math.min(0.85, dragRef.current.panFracStart + dPriceFracOfHalf));
      setPanFrac(next);
    }
  };
  const onMouseUp = (e) => { if (tryDrawingPointer(e, "up")) return; dragRef.current = null; };
  const onMouseLeave = () => { eraserHoverRef.current = null; dragRef.current = null; setPointer(null); setHoverBucket(null); setRangeTip(null); setTrendTip(null); setPatternTip(null); setCandleTip(null); };

  const onTouchStart = (e) => {
    if (e.touches.length === 1 && tryDrawingPointer(e, "down")) return;
    if (e.touches.length === 2) {
      const [a, b] = [e.touches[0], e.touches[1]];
      pinchRef.current = {
        dist: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
        midY: (a.clientY + b.clientY) / 2,
        zoomStart: zoomScale,
        panStart: panFrac,
        rect: e.currentTarget.getBoundingClientRect(),
      };
    } else if (e.touches.length === 1) {
      dragRef.current = { y: e.touches[0].clientY, panFracStart: panFrac, rect: e.currentTarget.getBoundingClientRect() };
      // Range tooltip tap detection (mobile) — single-tap on a Range line
      // shows the tooltip. Tap elsewhere dismisses it.
      const rect0 = e.currentTarget.getBoundingClientRect();
      const tx0 = e.touches[0].clientX - rect0.left;
      const ty0 = e.touches[0].clientY - rect0.top;
      const hit = rangeHitRef.current;
      if (hit && tx0 >= 0 && tx0 <= rect0.width - RIGHT_AXIS) {
        const HIT = 10;  // a bit more generous on mobile.
        if (Math.abs(ty0 - hit.hi.y) <= HIT) {
          setRangeTip({ kind: "hi", x: tx0, y: ty0, meta: hit.hi });
        } else if (Math.abs(ty0 - hit.lo.y) <= HIT) {
          setRangeTip({ kind: "lo", x: tx0, y: ty0, meta: hit.lo });
        } else {
          setRangeTip(null);
        }
      } else {
        setRangeTip(null);
      }
      // Trendline tap detection (mobile) — show tooltip on tap near line or
      // pivot. Tap elsewhere dismisses it.
      const tHit = trendHitRef.current;
      if (tHit && tx0 >= 0 && tx0 <= rect0.width - RIGHT_AXIS) {
        let onTrend = false;
        for (const pt of tHit.points) {
          if (Math.hypot(tx0 - pt.x, ty0 - pt.y) <= 14) { onTrend = true; break; }
        }
        if (!onTrend && tx0 >= tHit.xStart - 6 && tx0 <= tHit.xEnd + 6) {
          const n = candles.length || 1;
          const step = (rect0.width - RIGHT_AXIS) / n;
          const idxAtX = (tx0 - step / 2) / step;
          const priceOnLine = tHit.line.m * idxAtX + tHit.line.b;
          const chartH0 = rect0.height - BOTTOM_AXIS;
          const min = viewRange.min, max = viewRange.max;
          const yOnLine = chartH0 - ((priceOnLine - min) / (max - min || 1)) * chartH0;
          if (Math.abs(ty0 - yOnLine) <= 10) onTrend = true;
        }
        if (onTrend) {
          setTrendTip({ x: tx0, y: ty0 });
        } else {
          setTrendTip(null);
        }
      } else {
        setTrendTip(null);
      }
      // Patterns tap detection (mobile)
      const pHit = patternsHitRef.current;
      if (pHit && tx0 >= 0 && tx0 <= rect0.width - RIGHT_AXIS) {
        let onPattern = false;
        for (const pt of pHit.points) {
          if (Math.hypot(tx0 - pt.x, ty0 - pt.y) <= 14) { onPattern = true; break; }
        }
        if (!onPattern && pHit.neckSeg
            && tx0 >= pHit.neckSeg.x1 - 6 && tx0 <= pHit.neckSeg.x2 + 6
            && Math.abs(ty0 - pHit.neckSeg.y) <= 8) {
          onPattern = true;
        }
        if (!onPattern && pHit.isTriangle) {
          const n = candles.length || 1;
          const step = (rect0.width - RIGHT_AXIS) / n;
          const idxAtX = (tx0 - step / 2) / step;
          const chartH0 = rect0.height - BOTTOM_AXIS;
          const min = viewRange.min, max = viewRange.max;
          for (const seg of [pHit.highLineSeg, pHit.lowLineSeg]) {
            const price = seg.m * idxAtX + seg.b;
            const yLine = chartH0 - ((price - min) / (max - min || 1)) * chartH0;
            if (tx0 >= seg.x1 - 6 && tx0 <= seg.x2 + 6 && Math.abs(ty0 - yLine) <= 10) {
              onPattern = true; break;
            }
          }
        }
        if (!onPattern && pHit.isHCH && pHit.necklineSeg) {
          const seg = pHit.necklineSeg;
          if (tx0 >= seg.x1 - 6 && tx0 <= seg.x2 + 6) {
            const n = candles.length || 1;
            const step = (rect0.width - RIGHT_AXIS) / n;
            const idxAtX = (tx0 - step / 2) / step;
            const chartH0 = rect0.height - BOTTOM_AXIS;
            const min = viewRange.min, max = viewRange.max;
            const price = seg.m * idxAtX + seg.b;
            const yLine = chartH0 - ((price - min) / (max - min || 1)) * chartH0;
            if (Math.abs(ty0 - yLine) <= 10) onPattern = true;
          }
        }
        if (onPattern) {
          setPatternTip({ x: tx0, y: ty0 });
        } else {
          setPatternTip(null);
        }
      } else {
        setPatternTip(null);
      }
      // Candle-pattern tap detection (mobile) — closest marker within 14 px.
      const cHits0 = candlesHitRef.current;
      if (cHits0 && cHits0.length && tx0 >= 0 && tx0 <= rect0.width - RIGHT_AXIS) {
        let best = null, bestD = 15;
        for (const h of cHits0) {
          const d = Math.hypot(tx0 - h.x, ty0 - h.y);
          if (d <= bestD) { best = h; bestD = d; }
        }
        if (best) setCandleTip({ x: tx0, y: ty0, hit: best });
        else setCandleTip(null);
      } else {
        setCandleTip(null);
      }
      // Long-press (300ms) to show the detailed tooltip on touch devices.
      const rect = e.currentTarget.getBoundingClientRect();
      const tx = e.touches[0].clientX - rect.left;
      const ty = e.touches[0].clientY - rect.top;
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = setTimeout(() => {
        const chartH = rect.height - BOTTOM_AXIS;
        const chartW = rect.width - RIGHT_AXIS;
        if (ty >= 0 && ty <= chartH && tx >= 0 && tx <= chartW) {
          const priceAt = viewRange.max - (ty / chartH) * (viewRange.max - viewRange.min);
          const bucket = findNearestBucket(priceAt);
          if (bucket) {
            setHoverBucket({ mouseX: tx, mouseY: ty, bucket, priceAt, canvasW: rect.width, canvasH: rect.height });
          }
        }
      }, 300);
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 1 && tryDrawingPointer(e, "move")) return;
    if (e.touches.length === 2 && pinchRef.current) {
      const [a, b] = [e.touches[0], e.touches[1]];
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      const factor = dist / (pinchRef.current.dist || 1);
      const rect = pinchRef.current.rect;
      const chartH = rect.height - BOTTOM_AXIS;
      const midY = (a.clientY + b.clientY) / 2 - rect.top;
      const anchor = ((midY / chartH) - 0.5) * 2;
      // Apply zoom incrementally from pinch start.
      const halfRange = (range.max - range.min) / 2 || 1;
      const center0 = (range.min + range.max) / 2;
      const oldHalf = halfRange / Math.max(0.0001, pinchRef.current.zoomStart);
      const priceUnder = (center0 + pinchRef.current.panStart * halfRange) + anchor * oldHalf;
      const nextScale = Math.min(5, Math.max(1, pinchRef.current.zoomStart * factor));
      const newHalf = halfRange / nextScale;
      const newCenter = priceUnder - anchor * newHalf;
      setZoomScale(nextScale);
      setPanFrac(Math.max(-0.85, Math.min(0.85, (newCenter - center0) / halfRange)));
      e.preventDefault();
    } else if (e.touches.length === 1 && dragRef.current) {
      const dy = e.touches[0].clientY - dragRef.current.y;
      // If the finger moves >8px before the long-press fires, cancel the tooltip.
      if (Math.abs(dy) > 8 && longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null;
      }
      const rect = dragRef.current.rect;
      const halfRange = (range.max - range.min) / 2 || 1;
      const newHalf = halfRange / Math.max(0.0001, zoomScale);
      const dFrac = (dy / (rect.height - BOTTOM_AXIS)) * (newHalf / halfRange) * 2;
      setPanFrac(Math.max(-0.85, Math.min(0.85, dragRef.current.panFracStart + dFrac)));
    }
  };
  const onTouchEnd = (e) => {
    if (tryDrawingPointer(e, "up")) { dragRef.current = null; pinchRef.current = null; return; }
    dragRef.current = null; pinchRef.current = null;
    if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
    setHoverBucket(null);
  };

  // Crosshair drawing — runs in its own RAF when pointer changes.
  // The effect body is wrapped in a `redraw` closure stored in
  // `crossRedrawRef` so the central ResizeObserver can re-invoke it on
  // container size changes without restarting any animation. The data
  // triggers (pointer/viewRange/candles) are unchanged.
  useEffect(() => {
    const redraw = () => {
      const cnv = crossRef.current;
      if (!cnv) return;
      const ctx = cnv.getContext("2d");
      if (!ctx) return;
      const dpr = window.devicePixelRatio || 1;
      const r = cnv.getBoundingClientRect();
      cnv.width = r.width * dpr; cnv.height = r.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, r.width, r.height);
      if (!pointer) return;
      const chartW = r.width - RIGHT_AXIS;
      const chartH = r.height - BOTTOM_AXIS;
      if (pointer.x < 0 || pointer.x > chartW || pointer.y < 0 || pointer.y > chartH) return;
      ctx.strokeStyle = "rgba(255,255,255,0.2)";
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath(); ctx.moveTo(pointer.x, 0); ctx.lineTo(pointer.x, chartH); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, pointer.y); ctx.lineTo(chartW, pointer.y); ctx.stroke();
      ctx.setLineDash([]);
      // Tooltip top-left: price at cursor + time of underlying candle.
      const priceAt = viewRange.max - (pointer.y / chartH) * (viewRange.max - viewRange.min);
      let timeLabel = "";
      if (candles.length) {
        const step = chartW / candles.length;
        const i = Math.max(0, Math.min(candles.length - 1, Math.floor(pointer.x / step)));
        timeLabel = new Date(candles[i].t).toLocaleString([], { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
      }
      const label = `${fmtPrice(priceAt)}  ·  ${timeLabel}`;
      ctx.font = "bold 10px ui-monospace,monospace";
      const padX = 8, padY = 5;
      const tw = ctx.measureText(label).width + padX * 2;
      const th = 18;
      ctx.fillStyle = "rgba(8,5,8,0.9)";
      ctx.strokeStyle = "rgba(249,115,22,0.4)";
      ctx.lineWidth = 1;
      roundRect(ctx, 8, 8, tw, th, 4);
      ctx.fill(); ctx.stroke();
      ctx.fillStyle = "#e0e0e0";
      ctx.fillText(label, 8 + padX, 8 + th - padY);
    };
    crossRedrawRef.current = redraw;
    redraw();
  }, [pointer, viewRange.min, viewRange.max, candles]);

  // ───── Layer 4: Personal Liquidation Simulator ─────
  // 2026-06-15 — Renders entry + liquidation marker lines plus a soft
  // risk-zone band, with a 1Hz pulse on the liquidation line. Cleans up
  // and stops the RAF loop when `simulation` becomes null.
  useEffect(() => {
    const cnv = simRef.current;
    if (!cnv) return undefined;
    const ctx = cnv.getContext("2d");
    if (!ctx) return undefined;
    const dpr = window.devicePixelRatio || 1;
    const fitAndClear = () => {
      const r = cnv.getBoundingClientRect();
      cnv.width = r.width * dpr;
      cnv.height = r.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, r.width, r.height);
      return r;
    };
    if (!simulation || simulation.entryPrice == null || simulation.liqPrice == null) {
      fitAndClear();
      if (simRafRef.current) { cancelAnimationFrame(simRafRef.current); simRafRef.current = null; }
      return undefined;
    }
    const isLong = simulation.direction !== "SHORT";
    const entryCol = isLong ? "#00d084" : "#ff3b5c";
    const tick = () => {
      const r = cnv.getBoundingClientRect();
      cnv.width = r.width * dpr; cnv.height = r.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, r.width, r.height);
      const chartW = r.width - RIGHT_AXIS;
      const chartH = r.height - BOTTOM_AXIS;
      const yOfLocal = (price) => {
        const min = viewRange.min, max = viewRange.max;
        if (max <= min) return chartH;
        return chartH - ((price - min) / (max - min)) * chartH;
      };
      const yEntry = yOfLocal(simulation.entryPrice);
      const yLiq = yOfLocal(simulation.liqPrice);

      // Risk-zone band between entry & liquidation.
      const top = Math.min(yEntry, yLiq);
      const h = Math.abs(yEntry - yLiq);
      ctx.fillStyle = "rgba(255,255,255,0.03)";
      ctx.fillRect(0, top, chartW, h);

      // Entry line (solid).
      ctx.strokeStyle = entryCol;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(0, yEntry); ctx.lineTo(chartW, yEntry); ctx.stroke();
      // Entry badge (left side).
      roundRect(ctx, 8, yEntry - 9, 116, 16, 4);
      ctx.fillStyle = entryCol; ctx.fill();
      ctx.fillStyle = "#000";
      ctx.font = "bold 9px ui-monospace,monospace";
      ctx.fillText(`ENTRADA ${fmtPrice(simulation.entryPrice)}`, 12, yEntry + 2);

      // Pulse alpha 0.4 → 1.0 once per second.
      const t = (performance.now() / 1000) % 1;
      const pulse = 0.4 + 0.6 * (0.5 + 0.5 * Math.sin(t * Math.PI * 2));
      ctx.shadowColor = `rgba(255,255,255,${pulse})`;
      ctx.shadowBlur = 8;
      ctx.strokeStyle = `rgba(255,255,255,${pulse})`;
      ctx.lineWidth = 2;
      ctx.setLineDash([8, 4]);
      ctx.beginPath();
      ctx.moveTo(0, yLiq); ctx.lineTo(chartW, yLiq); ctx.stroke();
      ctx.setLineDash([]);
      ctx.shadowBlur = 0;
      // Liquidation badge.
      roundRect(ctx, 8, yLiq - 9, 116, 16, 4);
      ctx.fillStyle = `rgba(255,255,255,${0.85 + 0.15 * pulse})`;
      ctx.fill();
      ctx.fillStyle = "#000";
      ctx.font = "bold 9px ui-monospace,monospace";
      ctx.fillText(`💀 LIQ ${fmtPrice(simulation.liqPrice)}`, 12, yLiq + 2);

      // Arrow on right edge from entry → liquidation.
      const arrowX = chartW - 14;
      ctx.strokeStyle = entryCol;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(arrowX, yEntry); ctx.lineTo(arrowX, yLiq); ctx.stroke();
      ctx.fillStyle = entryCol;
      ctx.beginPath();
      const dir = yLiq > yEntry ? 1 : -1;
      ctx.moveTo(arrowX - 4, yLiq - 6 * dir);
      ctx.lineTo(arrowX + 4, yLiq - 6 * dir);
      ctx.lineTo(arrowX, yLiq);
      ctx.closePath();
      ctx.fill();

      simRafRef.current = requestAnimationFrame(tick);
    };
    simRafRef.current = requestAnimationFrame(tick);
    return () => { if (simRafRef.current) { cancelAnimationFrame(simRafRef.current); simRafRef.current = null; } };
  }, [simulation, viewRange.min, viewRange.max]);

  // ───── Layer 5: Whale Liquidations (golden ≥$1M, silver $100K–$1M) ─────
  // Renders dashed horizontal lines for clusters reported by
  // /api/liqmap/whales/{coin}. Pure presentational — no fetching here.
  // Wrapped in `redraw` closure stored in `whaleRedrawRef` so the central
  // ResizeObserver can re-invoke it on container size changes.
  useEffect(() => {
    const redraw = () => {
      const cnv = whaleRef.current;
      if (!cnv) return;
      const ctx = cnv.getContext("2d");
      if (!ctx) return;
      const dpr = window.devicePixelRatio || 1;
      const r = cnv.getBoundingClientRect();
      cnv.width = r.width * dpr; cnv.height = r.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, r.width, r.height);
      if (!whales) return;
      const chartW = r.width - RIGHT_AXIS;
      const chartH = r.height - BOTTOM_AXIS;
      const yOfLocal = (price) => {
        const min = viewRange.min, max = viewRange.max;
        if (max <= min) return chartH;
        return chartH - ((price - min) / (max - min)) * chartH;
      };

      // ─── Medium (silver, $100K–$1M) — draw FIRST so whales render on top.
      const mediums = Array.isArray(whales.medium_clusters) ? whales.medium_clusters : [];
      ctx.lineCap = "round";
      for (const c of mediums) {
        const intensity = Number(c.intensity) || 0;
        if (intensity < 0.15) continue;
        const y = yOfLocal(c.price);
        if (y < 0 || y > chartH) continue;
        const alpha = intensity * 0.5;
        const xStart = chartW * (1 - intensity) * 0.4;
        const xEnd = chartW * 0.98;
        const grad = ctx.createLinearGradient(xStart, 0, xEnd, 0);
        grad.addColorStop(0, "rgba(192,192,192,0)");
        grad.addColorStop(0.05, `rgba(192,192,192,${alpha})`);
        grad.addColorStop(0.95, `rgba(192,192,192,${alpha})`);
        grad.addColorStop(1, `rgba(192,192,192,${alpha * 0.5})`);
        ctx.strokeStyle = grad;
        ctx.lineWidth = Math.max(1, intensity * 2);
        ctx.setLineDash([5, 4]);
        ctx.beginPath();
        ctx.moveTo(xStart, y);
        ctx.lineTo(xEnd, y);
        ctx.stroke();
      }

      // ─── Whales (golden, ≥$1M)
      const whaleList = Array.isArray(whales.whale_clusters) ? whales.whale_clusters : [];
      for (const c of whaleList) {
        const intensity = Number(c.intensity) || 0;
        if (intensity < 0.1) continue;
        const y = yOfLocal(c.price);
        if (y < 0 || y > chartH) continue;
        const alpha = intensity * 0.9;
        const xStart = chartW * (1 - intensity) * 0.4;
        const xEnd = chartW * 0.98;
        const grad = ctx.createLinearGradient(xStart, 0, xEnd, 0);
        grad.addColorStop(0, "rgba(255,215,0,0)");
        grad.addColorStop(0.05, `rgba(255,215,0,${alpha})`);
        grad.addColorStop(0.95, `rgba(255,215,0,${alpha})`);
        grad.addColorStop(1, `rgba(255,215,0,${alpha * 0.5})`);
        ctx.strokeStyle = grad;
        ctx.lineWidth = Math.max(1.5, intensity * 4);
        ctx.setLineDash([10, 5]);
        ctx.beginPath();
        ctx.moveTo(xStart, y);
        ctx.lineTo(xEnd, y);
        ctx.stroke();

        // Label only on strong clusters.
        if (intensity > 0.5) {
          const vol = Number(c.volume_usd) || 0;
          const volText = vol >= 1e9
            ? `$${(vol / 1e9).toFixed(1)}B`
            : vol >= 1e6
            ? `$${(vol / 1e6).toFixed(0)}M`
            : `$${(vol / 1e3).toFixed(0)}K`;
          ctx.setLineDash([]);
          ctx.fillStyle = "rgba(255,215,0,0.9)";
          ctx.font = "bold 9px ui-monospace,monospace";
          ctx.fillText(`🐋 ${volText}`, 10, y - 3);
        }
      }
      ctx.setLineDash([]);
    };
    whaleRedrawRef.current = redraw;
    redraw();
  }, [whales, viewRange.min, viewRange.max]);

  // ───── Layer 5.5: EMAs (9/21/50/200) + 3 cross detections ─────
  // Pure derived data — recomputed when candles change. Crosses are
  // recomputed alongside because they depend on the same closes array;
  // splitting them would re-walk it. Coin/timeframe are inputs to the
  // detector for relevance gating (see emaMath.js).
  const emaSeries = useMemo(() => {
    if (!showEMA || !Array.isArray(candles) || candles.length < 21) return null;
    const closes = candles.map((c) => c.c);
    return {
      e9:   computeEMA(closes, 9),
      e21:  computeEMA(closes, 21),
      e50:  computeEMA(closes, 50),
      e200: computeEMA(closes, 200),
    };
  }, [showEMA, candles]);

  const emaCrosses = useMemo(() => {
    if (!showEMA || !Array.isArray(candles) || candles.length < 21) return [];
    return detectAllEMACrosses(candles, coin, timeframe);
  }, [showEMA, candles, coin, timeframe]);

  useEffect(() => {
    const redraw = () => {
      const cnv = emaRef.current;
      if (!cnv) return;
      const ctx = cnv.getContext("2d");
      if (!ctx) return;
      const dpr = window.devicePixelRatio || 1;
      const r = cnv.getBoundingClientRect();
      cnv.width = r.width * dpr; cnv.height = r.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, r.width, r.height);
      // Neither EMA nor Stochastic requested? clean canvas already; exit.
      if ((!showEMA || !emaSeries) && !showStochastic) return;
      if (!candles.length) return;
      const chartW = r.width - RIGHT_AXIS;
      const chartH = r.height - BOTTOM_AXIS;
      const min = viewRange.min, max = viewRange.max;
      if (max <= min) return;
      const yOf = (price) => chartH - ((price - min) / (max - min)) * chartH;
      // Visible candle index window — same convention as the rest of
      // the chart (the heatmap and S/R layer treat candles[0..n-1] as
      // mapped linearly across chartW).
      const n = candles.length;
      const xOf = (i) => (i / (n - 1)) * chartW;

      // ── Stochastic cross markers (drawn under EMA lines when both on).
      // Each `stochasticCrosses` entry has {idx, dir, zone, k, d, ts}. We
      // paint a small filled circle at (xOf(idx), yOf(close[idx])) with a
      // ring in the direction color, matching the EMA cross visual weight.
      if (showStochastic && Array.isArray(stochasticCrosses) && stochasticCrosses.length) {
        for (const cr of stochasticCrosses) {
          if (!Number.isFinite(cr?.idx)) continue;
          const px = candles[cr.idx]?.c;
          if (!Number.isFinite(px)) continue;
          const x = xOf(cr.idx);
          const y = yOf(px);
          if (y < -20 || y > chartH + 20) continue;
          const isBull = cr.dir === "bull";
          const fill = isBull ? "#10b981" : "#ef4444"; // green / red
          const ring = isBull ? "rgba(16,185,129,0.35)" : "rgba(239,68,68,0.35)";
          // Outer ring
          ctx.beginPath();
          ctx.arc(x, y, 6, 0, Math.PI * 2);
          ctx.fillStyle = ring;
          ctx.fill();
          // Inner dot
          ctx.beginPath();
          ctx.arc(x, y, 2.5, 0, Math.PI * 2);
          ctx.fillStyle = fill;
          ctx.fill();
          // Micro label ("K" over dot for stochastic origin — small)
          ctx.font = "bold 8px monospace";
          ctx.fillStyle = fill;
          ctx.textAlign = "center";
          ctx.textBaseline = "bottom";
          ctx.fillText("Stoch", x, y - 8);
          ctx.textAlign = "start";
          ctx.textBaseline = "alphabetic";
        }
      }

      // If EMA layer is not active, stop here — the stochastic markers
      // above were the only thing to draw on this canvas.
      if (!showEMA || !emaSeries) return;

      // Paleta por período: corta = brillante (cyan), larga = más sobria
      // (indigo). Mismo lenguaje visual que el resto de overlays —
      // intensidad y dash conservados como pista de jerarquía temporal.
      //          color           dash               label
      const lines = [
        { arr: emaSeries.e9,   color: "#7dd3fc",      dash: [],        label: "EMA 9"   }, // sky-300 (brightest of the blue ramp)
        { arr: emaSeries.e21,  color: "#3b82f6",      dash: [],        label: "EMA 21"  }, // blue-500
        { arr: emaSeries.e50,  color: "#1e40af",      dash: [],        label: "EMA 50"  }, // blue-800
        { arr: emaSeries.e200, color: "#475569",      dash: [],        label: "EMA 200" }, // slate-600 (most muted — slowest)
      ];

      // Draw the 4 EMA lines.
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      for (const ln of lines) {
        if (!ln.arr) continue;
        ctx.strokeStyle = ln.color;
        ctx.lineWidth = 1.4;
        ctx.setLineDash(ln.dash);
        ctx.beginPath();
        let started = false;
        for (let i = 0; i < n; i++) {
          const v = ln.arr[i];
          if (!Number.isFinite(v)) continue;
          const x = xOf(i);
          const y = yOf(v);
          if (y < -50 || y > chartH + 50) {
            started = false; // skip far-offscreen segments to keep paths short
            continue;
          }
          if (!started) { ctx.moveTo(x, y); started = true; }
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      ctx.setLineDash([]);

      // ─── Right-edge mini labels (period only, no value — value would
      // clutter the already busy right axis used by S/R levels). Stacked
      // by EMA value to avoid overlap; small font, same color as the
      // line they label.
      ctx.font = "bold 9px ui-monospace,monospace";
      ctx.textBaseline = "middle";
      const labelEntries = lines
        .map((ln) => {
          const v = ln.arr ? ln.arr[ln.arr.length - 1] : null;
          if (!Number.isFinite(v)) return null;
          return { ...ln, lastValue: v, y: yOf(v) };
        })
        .filter(Boolean)
        .sort((a, b) => a.y - b.y);
      // Anti-overlap pass: keep labels at least 11px apart vertically.
      let lastY = -Infinity;
      for (const lab of labelEntries) {
        if (lab.y - lastY < 11) lab.y = lastY + 11;
        lastY = lab.y;
        if (lab.y < 4 || lab.y > chartH - 4) continue;
        ctx.fillStyle = lab.color;
        ctx.fillText(lab.label, chartW + 4, lab.y);
      }

      // ─── Cross markers — same colour family + dash convention as
      // chartPatterns rendering (L1885+). Confirmed crosses get a solid
      // full-intensity stamp, forming get a dashed dim version,
      // invalidated get a gray X. Pattern names are explicit (the
      // detector returns ema_9_21_bull/bear etc.) so no genericness.
      const PT_COL = "#8b5cf6";       // violet-500 — distinct from S/R/Patterns/Whales
      const PT_COL_DIM = "rgba(139,92,246,0.5)";
      const PT_INV_COL = "#9ca3af";   // slate-400 — gray for invalidated
      const HALO = "rgba(255,255,255,0.40)";
      ctx.font = "bold 10px ui-monospace,monospace";

      for (const cross of emaCrosses) {
        if (cross.idx_cross == null) continue;
        if (cross.idx_cross < 0 || cross.idx_cross >= n) continue;
        const x = xOf(cross.idx_cross);
        const y = yOf((cross.fast_value + cross.slow_value) / 2);
        if (y < 0 || y > chartH) continue;
        const isBull = cross.pattern === "golden_cross" ||
                       cross.pattern === "ema_9_21_bull" ||
                       cross.pattern === "ema_21_50_bull";
        const confirmed = cross.status === "confirmed";
        const forming = cross.status === "forming";
        const invalidated = cross.status === "invalidated";
        const mainCol = invalidated ? PT_INV_COL : PT_COL;
        const dimCol  = invalidated ? "rgba(156,163,175,0.55)" : PT_COL_DIM;

        // Halo (white glow) for contrast against the dark heatmap.
        ctx.fillStyle = HALO;
        ctx.beginPath();
        ctx.arc(x, y, 7, 0, Math.PI * 2);
        ctx.fill();

        // Main circle — solid for confirmed, dashed-stroke for forming,
        // gray for invalidated. Direction shown by arrow inside.
        ctx.lineWidth = confirmed ? 2.4 : (forming ? 2.0 : 2.0);
        ctx.setLineDash(confirmed ? [] : (forming ? [3, 2] : [4, 3]));
        ctx.strokeStyle = confirmed ? mainCol : dimCol;
        ctx.fillStyle = "rgba(8,5,8,0.85)";
        ctx.beginPath();
        ctx.arc(x, y, 5.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.setLineDash([]);

        // Arrow ▲ / ▼ inside the circle indicates bull/bear.
        ctx.fillStyle = confirmed ? mainCol : dimCol;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(isBull ? "▲" : "▼", x, y + 0.5);
        ctx.textAlign = "left";

        // Compact label next to the marker for confirmed crosses only —
        // forming/invalidated stay marker-only to avoid label clutter.
        if (confirmed) {
          let labelTxt;
          if (cross.pattern === "golden_cross") labelTxt = "GOLDEN";
          else if (cross.pattern === "death_cross") labelTxt = "DEATH";
          else if (cross.pattern === "ema_21_50_bull") labelTxt = "21×50 BULL";
          else if (cross.pattern === "ema_21_50_bear") labelTxt = "21×50 BEAR";
          else if (cross.pattern === "ema_9_21_bull")  labelTxt = "9×21 BULL";
          else if (cross.pattern === "ema_9_21_bear")  labelTxt = "9×21 BEAR";
          else labelTxt = cross.pattern;
          // Bumped from 9px → 11px for legibility without zoom (the
          // previous 9px was unreadable at typical desktop densities).
          ctx.font = "bold 11px ui-monospace,monospace";
          const tw = ctx.measureText(labelTxt).width;
          const padX = 4;
          const padY = 3;
          const bw = tw + padX * 2;   // badge width
          const bh = 14;              // badge height (matches 11px font + padding)
          // Default position: to the RIGHT of the marker. If that would
          // spill into the right-axis price-label gutter, flip to the
          // LEFT side of the marker so the badge never overlaps the
          // YAxis labels. Same clamp applies vertically against the
          // bottom axis.
          let lx = x + 8;
          let ly = y - 12;
          // Right-edge clamp: chartW is the inner chart width (excludes
          // RIGHT_AXIS gutter). Leave a 4px breathing margin.
          if (lx + bw > chartW - 4) {
            lx = x - 8 - bw;
          }
          // Top-edge clamp.
          if (ly - bh < 2) ly = bh + 2;
          // Bottom-edge clamp (avoid colliding with x-axis labels).
          if (ly + padY > chartH - 4) ly = chartH - 4 - padY;
          // Left-edge clamp (paranoid fallback when both flips would
          // still spill — pin to chart edge).
          if (lx < 2) lx = 2;
          ctx.fillStyle = "rgba(8,5,8,0.88)";
          ctx.strokeStyle = mainCol;
          ctx.lineWidth = 1;
          roundRect(ctx, lx - 1, ly - bh + 1, bw + 2, bh, 3);
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle = mainCol;
          ctx.fillText(labelTxt, lx + padX, ly);
        }
      }
      ctx.textBaseline = "alphabetic";
    };
    emaRedrawRef.current = redraw;
    redraw();
  }, [showEMA, emaSeries, emaCrosses, candles, viewRange.min, viewRange.max, showStochastic, stochasticCrosses]);


  // ───── Layer 6: Support / Resistance IA + Trading Range (toggled) ─────
  // Two independent overlays sharing the same canvas:
  //   (a) S/R IA — dashed levels with tier hierarchy (drawn when `srData`).
  //   (b) Trading Range — two cyan dotted lines marking the high/low of
  //       the currently displayed candles (reactive to brush sub-range).
  // The Range is drawn AFTER the S/R levels so its labels float on top
  // when they collide, and its color (cyan) is reserved exclusively for
  // it — never used by S/R (red/green).
  useEffect(() => {
    const redraw = () => {
    const cnv = srRef.current;
    if (!cnv) return undefined;
    const ctx = cnv.getContext("2d");
    if (!ctx) return undefined;
    const dpr = window.devicePixelRatio || 1;
    const r = cnv.getBoundingClientRect();
    cnv.width = r.width * dpr; cnv.height = r.height * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, r.width, r.height);
    if (!srData && !showRange && !showTrendline && !showPatterns && !showCandles) {
      rangeHitRef.current = null;
      trendHitRef.current = null;
      patternsHitRef.current = null;
      candlesHitRef.current = [];
      return undefined;
    }
    const chartW = r.width - RIGHT_AXIS;
    const chartH = r.height - BOTTOM_AXIS;
    const yOfLocal = (price) => {
      const min = viewRange.min, max = viewRange.max;
      if (max <= min) return chartH;
      return chartH - ((price - min) / (max - min)) * chartH;
    };
    const drawLevel = (level, isResistance) => {
      const y = yOfLocal(level.precio);
      if (y < 0 || y > chartH) return;
      const tipo = (level.tipo || "media").toLowerCase();
      const baseCol = isResistance
        ? (tipo === "fuerte" ? "#ff3b5c" : tipo === "media" ? "#ff8c42" : "#ffb380")
        : (tipo === "fuerte" ? "#00d084" : tipo === "media" ? "#4ade80" : "#86efac");
      // Opacity hierarchy: FUERTE 1.0 / MEDIA 0.70 / DEBIL 0.40 — bigger
      // gap between debil and fuerte than before so the strong levels really
      // pop against the weak ones in the heatmap.
      const tierAlpha = tipo === "fuerte" ? 1.0 : tipo === "media" ? 0.70 : 0.40;
      ctx.save();
      ctx.globalAlpha = tierAlpha;
      // Subtle glow ring only for FUERTE so the strongest levels stand out
      // without polluting the chart with effects everywhere.
      if (tipo === "fuerte") {
        ctx.shadowColor = baseCol;
        ctx.shadowBlur = 6;
      }
      ctx.strokeStyle = baseCol;
      ctx.lineWidth = tipo === "fuerte" ? 2.5 : tipo === "media" ? 1.5 : 1.0;
      ctx.setLineDash([12, 6]);
      ctx.beginPath();
      ctx.moveTo(0, y); ctx.lineTo(chartW, y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.shadowBlur = 0;
      ctx.globalAlpha = 1.0;  // Reset for label rendering.
      const prefix = level.confirmado_por_liquidaciones
        ? (isResistance ? "🔒 R " : "🔒 S ")
        : (isResistance ? "R " : "S ");
      // Build the label: prefix + price + optional historical success rate.
      const rawPct = level.tasa_acierto_pct;
      const showPct = rawPct !== null && rawPct !== undefined && Number.isFinite(Number(rawPct));
      const pct = showPct ? Number(rawPct) : 0;
      const touches = Number(level.num_toques) || 0;
      const touchTxt = touches > 0 ? `·${touches}t` : "";
      const pctTxt = showPct ? ` ${pct}%${touchTxt}` : "";
      // Strength score chip (new in Fase 2): compact `[S87]` after the
      // price so the user can read the composite score at a glance.
      const scoreNum = Number(level.strength_score);
      const scoreTxt = Number.isFinite(scoreNum) ? ` ⬢${Math.round(scoreNum)}` : "";
      ctx.font = "bold 10px Inter, ui-sans-serif";
      const label = `${prefix}${fmtPrice(level.precio)}${pctTxt}${scoreTxt}`;
      const textW = ctx.measureText(label).width;
      // Background pill so the label stays readable over the heatmap.
      const padX = 5, padY = 2;
      const bx = chartW - textW - padX * 2 - 10;
      const by = y - 8;
      ctx.fillStyle = "rgba(8,5,8,0.85)";
      roundRect(ctx, bx, by, textW + padX * 2, 16, 4);
      ctx.fill();
      ctx.strokeStyle = baseCol;
      ctx.globalAlpha = Math.max(tierAlpha, 0.6); // Keep label legible.
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.globalAlpha = 1.0;
      // Color the % bracket distinctly when reliability is high (≥60%).
      ctx.fillStyle = baseCol;
      ctx.fillText(label, bx + padX, by + 12);
      if (showPct && pct >= 60) {
        // Overpaint just the percentage in white for emphasis on high-reliability levels.
        const preLabel = `${prefix}${fmtPrice(level.precio)} `;
        const preW = ctx.measureText(preLabel).width;
        ctx.fillStyle = "#fff";
        ctx.fillText(`${pct}%${touchTxt}`, bx + padX + preW, by + 12);
      }
      ctx.restore();
    };
    for (const lv of (srData?.resistencias || [])) drawLevel(lv, true);
    for (const lv of (srData?.soportes || [])) drawLevel(lv, false);

    // ── Trading Range layer (independent toggle) ─────────────────────
    // Two cyan dotted lines for the high/low of the currently displayed
    // candles. Reactive to brush sub-range because `candles` here IS the
    // already-sliced array (LiqMapPage passes `displayedCandles`). Cyan
    // color reserved exclusively for this — never used by S/R.
    if (!showRange) rangeHitRef.current = null;
    if (showRange && candles.length) {
      let hi = -Infinity, lo = Infinity;
      let hiT = null, loT = null;
      for (const c of candles) {
        if (Number.isFinite(c.h) && c.h > hi) { hi = c.h; hiT = c.t; }
        if (Number.isFinite(c.l) && c.l < lo) { lo = c.l; loT = c.t; }
      }
      if (Number.isFinite(hi) && Number.isFinite(lo) && hi > lo) {
        const RANGE_COL = "#22d3ee";  // cyan-400; reserved for Range only.
        // Update hit-zones for the pointer tooltip. These are the y-coords
        // of the two horizontal lines drawn below; pointer handlers read
        // this ref to detect if the cursor is hovering one of them.
        rangeHitRef.current = {
          hi: { y: yOfLocal(hi), t: hiT, price: hi },
          lo: { y: yOfLocal(lo), t: loT, price: lo },
        };
        const drawRangeLine = (price, labelTxt, displayValue) => {
          const y = yOfLocal(price);
          if (y < -10 || y > chartH + 10) return;
          ctx.save();
          ctx.strokeStyle = RANGE_COL;
          ctx.lineWidth = 1.2;
          ctx.setLineDash([2, 4]);  // Dotted — distinct from S/R dashed [12,6].
          ctx.globalAlpha = 0.95;
          ctx.beginPath();
          ctx.moveTo(0, y); ctx.lineTo(chartW, y);
          ctx.stroke();
          ctx.setLineDash([]);
          // Label on the LEFT to avoid colliding with S/R labels on the right.
          ctx.font = "bold 10px Inter, ui-sans-serif";
          // Always-conservative integer format for Range: floor for low,
          // ceil for high. Guarantees label_min <= real_low and
          // label_max >= real_high so the wick is never "outside" the label.
          const label = `${labelTxt} $${displayValue.toLocaleString()}`;
          const textW = ctx.measureText(label).width;
          const padX = 5;
          const bx = 6;
          const by = y - 8;
          ctx.fillStyle = "rgba(8,5,8,0.88)";
          roundRect(ctx, bx, by, textW + padX * 2, 16, 4);
          ctx.fill();
          ctx.strokeStyle = RANGE_COL;
          ctx.lineWidth = 1;
          ctx.stroke();
          ctx.fillStyle = RANGE_COL;
          ctx.fillText(label, bx + padX, by + 12);
          ctx.restore();
        };
        drawRangeLine(hi, "↑ MÁX RANGO", Math.ceil(hi));
        drawRangeLine(lo, "↓ MÍN RANGO", Math.floor(lo));
      }
    }

    // ── Trendline layer (independent toggle) ─────────────────────────
    // Diagonal violet line through 2-3+ swing lows (uptrend) or highs
    // (downtrend). Detects ruptures and renders the broken segment with
    // dotted/dimmed style. Honest "Sin tendencia" overlay when there is
    // not enough data — never invents a line.
    if (!showTrendline) {
      trendHitRef.current = null;
    }
    if (showTrendline && candles.length) {
      const tlRes = computeTrendline(candles);
      if (tlRes.trend) {
        const tl = tlRes.trend;
        const TL_COL = "#a855f7"; // violet-500; reserved for Trendline only.
        const n = candles.length;
        // X mapping: same convention used by other layers — visible candle
        // pixel grid spans [0..chartW), one slot per candle.
        const visW = chartW;
        const step = visW / n;
        const xOfIdx = (idx) => idx * step + step / 2;
        const projectionEndIdx = n - 1; // extend to right edge of candle area.
        // Compute the 3 line segments:
        //   1) [firstPivot → lastPivot]            → "anchored" (solid)
        //   2) [lastPivot → breakIdx or end]       → "active projection" (solid)
        //   3) [breakIdx → end]                    → "broken" (dotted, dimmed)
        const x0 = xOfIdx(tl.firstPivotIdx);
        const y0 = yOfLocal(tl.line.m * tl.firstPivotIdx + tl.line.b);

        const liveEndIdx = tl.breakIdx != null ? tl.breakIdx : projectionEndIdx;
        const xLive = xOfIdx(liveEndIdx);
        const yLive = yOfLocal(tl.line.m * liveEndIdx + tl.line.b);

        ctx.save();
        // Live (vigente) segment — solid violet.
        ctx.strokeStyle = TL_COL;
        ctx.lineWidth = 1.8;
        ctx.setLineDash([]);
        ctx.globalAlpha = 1.0;
        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.lineTo(xLive, yLive);
        ctx.stroke();

        // Broken segment (if any) — dotted, low opacity.
        if (tl.breakIdx != null && tl.breakIdx < projectionEndIdx) {
          const xEnd = xOfIdx(projectionEndIdx);
          const yEnd = yOfLocal(tl.line.m * projectionEndIdx + tl.line.b);
          ctx.strokeStyle = TL_COL;
          ctx.lineWidth = 1.2;
          ctx.setLineDash([2, 4]);
          ctx.globalAlpha = 0.35;
          ctx.beginPath();
          ctx.moveTo(xLive, yLive);
          ctx.lineTo(xEnd, yEnd);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.globalAlpha = 1.0;
        }

        // Pivot dots — small violet circles for transparency.
        const pivotScreenPts = [];
        for (const piv of tl.points) {
          const px = xOfIdx(piv.i);
          const py = yOfLocal(piv.p);
          const isAnchor = piv.i === tl.firstPivotIdx || piv.i === tl.lastPivotIdx;
          // Always push to hit-data (even off-screen) so the tooltip lists
          // every pivot; visibility on canvas remains conditional.
          pivotScreenPts.push({
            x: px, y: py, i: piv.i, p: piv.p, t: piv.t,
            role: isAnchor ? "anchor" : "intermediate",
            kind: tl.kind, // "up" → swing low, "down" → swing high
          });
          if (py < -5 || py > chartH + 5) continue;
          ctx.fillStyle = TL_COL;
          ctx.beginPath();
          ctx.arc(px, py, isAnchor ? 4 : 3, 0, Math.PI * 2);
          ctx.fill();
          // Subtle inner ring on anchors to differentiate them visually.
          if (isAnchor) {
            ctx.strokeStyle = "rgba(255,255,255,0.85)";
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.arc(px, py, 2, 0, Math.PI * 2);
            ctx.stroke();
          }
        }
        // Publish hit-data for pointer handlers — line equation, segment
        // x-bounds (only x∈[xStart..xEnd] should activate the tooltip),
        // and the full ordered pivot list with anchor/intermediate role.
        trendHitRef.current = {
          kind: tl.kind,
          line: { m: tl.line.m, b: tl.line.b },
          xStart: x0,
          xEnd: tl.breakIdx != null ? xOfIdx(projectionEndIdx) : xLive,
          points: pivotScreenPts,
          breakIdx: tl.breakIdx,
          breakPrice: tl.breakPrice,
        };

        // Label near first pivot — includes coverage metadata so the
        // user immediately knows which window the line represents:
        //   "↗ Tendencia alcista · desde 25 jun · Δ+$1,496"
        // First-pivot date is formatted in es-ES short day+month. Delta is
        // (last.p − first.p), prefixed with sign, rendered with the same
        // floor/ceil-equivalent rounding (no decimals when ≥$100, else 2dp).
        const first = tl.points[0];
        const last = tl.points[tl.points.length - 1];
        const delta = last.p - first.p;
        const sign = delta >= 0 ? "+" : "−";
        const absDelta = Math.abs(delta);
        const deltaTxt = absDelta >= 100
          ? `$${Math.round(absDelta).toLocaleString()}`
          : `$${absDelta.toFixed(2)}`;
        let fromTxt = "";
        if (first.t) {
          try {
            fromTxt = new Date(first.t).toLocaleDateString("es-ES", {
              day: "numeric", month: "short",
            }).replace(".", "");
          } catch { /* ignore */ }
        }
        const dir = tl.kind === "up" ? "↗ Tendencia alcista" : "↘ Tendencia bajista";
        const parts = [dir];
        if (fromTxt) parts.push(`desde ${fromTxt}`);
        parts.push(`Δ${sign}${deltaTxt}`);
        const labelText = parts.join(" · ");
        ctx.font = "bold 10px Inter, ui-sans-serif";
        const labW = ctx.measureText(labelText).width;
        const padX = 5;
        const lbx = Math.min(Math.max(x0 + 8, 6), chartW - labW - padX * 2 - 6);
        const lby = Math.min(Math.max(y0 - 22, 4), chartH - 20);
        ctx.fillStyle = "rgba(8,5,8,0.88)";
        roundRect(ctx, lbx, lby, labW + padX * 2, 16, 4);
        ctx.fill();
        ctx.strokeStyle = TL_COL;
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.fillStyle = TL_COL;
        ctx.fillText(labelText, lbx + padX, lby + 12);

        // Break marker — small ✗ at the exact break point.
        if (tl.breakIdx != null) {
          const xb = xOfIdx(tl.breakIdx);
          const yb = yOfLocal(tl.breakPrice);
          if (yb > -5 && yb < chartH + 5) {
            const brkLabel = `✗ ROTA ${fmtPrice(tl.breakPrice)}`;
            ctx.font = "bold 10px Inter, ui-sans-serif";
            const brkW = ctx.measureText(brkLabel).width;
            const bbx = Math.min(xb + 6, chartW - brkW - padX * 2 - 4);
            const bby = Math.min(yb + 6, chartH - 20);
            ctx.fillStyle = "rgba(8,5,8,0.88)";
            roundRect(ctx, bbx, bby, brkW + padX * 2, 16, 4);
            ctx.fill();
            ctx.strokeStyle = TL_COL;
            ctx.lineWidth = 1;
            ctx.stroke();
            ctx.fillStyle = TL_COL;
            ctx.fillText(brkLabel, bbx + padX, bby + 12);
            // X glyph at the exact break price intersection.
            ctx.strokeStyle = TL_COL;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(xb - 4, yb - 4); ctx.lineTo(xb + 4, yb + 4);
            ctx.moveTo(xb + 4, yb - 4); ctx.lineTo(xb - 4, yb + 4);
            ctx.stroke();
          }
        }
        ctx.restore();
      } else {
        // No trend detected — discrete "Sin tendencia clara" overlay.
        // Position: BOTTOM-LEFT of the chart area. Top-left is occupied by
        // the cluster MAX/SUP labels (drawn at x=8 y≈cluster_max_price),
        // top-right by livePrice + zoom controls. Bottom-left is empty.
        trendHitRef.current = null;
        ctx.save();
        ctx.font = "italic 10px Inter, ui-sans-serif";
        const msg = "Sin tendencia clara detectada";
        const msgW = ctx.measureText(msg).width;
        const padX = 6;
        const mbx = 8;
        const mby = chartH - 22; // just above the time axis baseline.
        ctx.fillStyle = "rgba(8,5,8,0.85)";
        roundRect(ctx, mbx, mby, msgW + padX * 2, 16, 4);
        ctx.fill();
        ctx.strokeStyle = "rgba(168,85,247,0.5)"; // dim violet border
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.fillStyle = "rgba(168,85,247,0.85)";
        ctx.fillText(msg, mbx + padX, mby + 12);
        ctx.restore();
      }
    }

    // ── Patterns layer (Double Top / Double Bottom) ──────────────────
    // Independent toggle. Magenta palette (#ec4899) so it never collides
    // with S/R red/green, Range cyan or Trendline violet. Uses
    // chartPatterns.detectDoubleTopBottom() which internally reuses
    // trendlineMath.detectSwings() — no duplicated swing logic.
    if (!showPatterns) {
      patternsHitRef.current = null;
    }
    if (showPatterns && candles.length) {
      // Run detectors in priority order. Double Top/Bottom (terminal,
      // 3 anchors with symmetric peak/bottom) is most specific. Falls
      // through to Triangle (continuation, 2 converging lines) and
      // finally to Head & Shoulders / Inverse H&S (3 pivots with
      // diagonal neckline). Each successive detector runs ONLY if the
      // previous returned null pattern.
      const dRes = detectDoubleTopBottom(candles, coin);
      const tRes = dRes.pattern ? null : detectTriangle(candles, coin);
      const hRes = (dRes.pattern || tRes?.pattern) ? null : detectHeadAndShoulders(candles, coin);
      const ihRes = (dRes.pattern || tRes?.pattern || hRes?.pattern) ? null : detectInverseHeadAndShoulders(candles, coin);
      const pRes = dRes.pattern ? dRes : tRes?.pattern ? tRes : hRes?.pattern ? hRes : ihRes || hRes || tRes || dRes;
      if (pRes.pattern === "double_top" || pRes.pattern === "double_bottom") {
        const PT_COL = "#ec4899";       // magenta (Tailwind pink-500)
        const PT_COL_DIM = "rgba(236,72,153,0.5)";
        const PT_INV_COL = "#9ca3af";   // slate-400 — gray for invalidated
        const PT_INV_COL_DIM = "rgba(156,163,175,0.55)";
        const HALO = "rgba(255,255,255,0.40)";  // white halo for contrast
        const isTop = pRes.pattern === "double_top";
        const status = pRes.status;
        const confirmed = status === "confirmed";
        const invalidated = status === "invalidated";
        const mainCol = invalidated ? PT_INV_COL : PT_COL;
        const dimCol  = invalidated ? PT_INV_COL_DIM : PT_COL_DIM;
        const a1 = isTop ? pRes.peak1   : pRes.bottom1;
        const aM = isTop ? pRes.valley  : pRes.ridge;
        const a2 = isTop ? pRes.peak2   : pRes.bottom2;
        const n = candles.length;
        const visW = chartW;
        const step = visW / n;
        const xOfIdx = (idx) => idx * step + step / 2;
        const x1 = xOfIdx(a1.i);
        const xm = xOfIdx(aM.i);
        const x2 = xOfIdx(a2.i);
        const yOfLocal = (price) => {
          const min = viewRange.min, max = viewRange.max;
          return chartH - ((price - min) / (max - min || 1)) * chartH;
        };
        const y1 = yOfLocal(a1.p);
        const ym = yOfLocal(aM.p);
        const y2 = yOfLocal(a2.p);
        const yNeck = yOfLocal(pRes.neckline);

        ctx.save();
        // ── Connector polyline LS→valley/ridge→RS with white halo underlay
        //    for contrast against the saturated heatmap bands. Draw halo
        //    first (thicker, white-translucent), then the magenta/gray
        //    line on top at the user-approved 2.4 px / dashed forming.
        const drawConnectorPath = () => {
          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(xm, ym);
          ctx.lineTo(x2, y2);
        };
        // Halo
        ctx.setLineDash([]);
        ctx.lineCap = "round"; ctx.lineJoin = "round";
        ctx.strokeStyle = HALO;
        ctx.lineWidth = 4;
        drawConnectorPath(); ctx.stroke();
        // Foreground
        ctx.strokeStyle = confirmed ? mainCol : dimCol;
        ctx.lineWidth = confirmed ? 2.4 : invalidated ? 2.0 : 2.0;
        ctx.setLineDash(confirmed ? [] : invalidated ? [6, 4] : [5, 4]);
        drawConnectorPath(); ctx.stroke();

        // ── Horizontal neckline extending past peak2 ─────────────────
        const xNeckEnd = Math.min(chartW - 4, x2 + step * 6);
        ctx.strokeStyle = HALO;
        ctx.lineWidth = 3;
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(x1, yNeck); ctx.lineTo(xNeckEnd, yNeck);
        ctx.stroke();
        ctx.setLineDash([2, 3]);
        ctx.strokeStyle = confirmed ? mainCol : dimCol;
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.moveTo(x1, yNeck); ctx.lineTo(xNeckEnd, yNeck);
        ctx.stroke();
        ctx.setLineDash([]);

        // ── Break marker (✗) on confirmed breakdown candle ───────────
        if (confirmed && pRes.breakIdx != null) {
          const xb = xOfIdx(pRes.breakIdx);
          const yb = yOfLocal(pRes.breakPrice);
          ctx.strokeStyle = mainCol;
          ctx.lineWidth = 2.4;
          ctx.beginPath();
          ctx.moveTo(xb - 5, yb - 5); ctx.lineTo(xb + 5, yb + 5);
          ctx.moveTo(xb + 5, yb - 5); ctx.lineTo(xb - 5, yb + 5);
          ctx.stroke();
        }
        // ── Invalidation marker (✗ red) on the candle that broke contrary ─
        if (invalidated && pRes.invalidationIdx != null) {
          const xi = xOfIdx(pRes.invalidationIdx);
          const yi = yOfLocal(pRes.invalidationPrice);
          ctx.strokeStyle = "#f87171"; // red-400 — clearly distinct from gray
          ctx.lineWidth = 2.4;
          ctx.beginPath();
          ctx.moveTo(xi - 5, yi - 5); ctx.lineTo(xi + 5, yi + 5);
          ctx.moveTo(xi + 5, yi - 5); ctx.lineTo(xi - 5, yi + 5);
          ctx.stroke();
        }

        // ── Anchor dots with shadow ──────────────────────────────────
        // Sizes per Punto D: peak 4→6 px, valley/ridge 3.5→5 px.
        const drawDot = (x, y, r) => {
          ctx.save();
          ctx.shadowBlur = 6;
          ctx.shadowColor = "rgba(0,0,0,0.6)";
          ctx.fillStyle = mainCol;
          ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
          ctx.restore();
          // Inner contrast ring (no shadow, sits on top of the dot).
          ctx.strokeStyle = "rgba(255,255,255,0.92)";
          ctx.lineWidth = 1.2;
          ctx.beginPath(); ctx.arc(x, y, Math.max(1, r - 2), 0, Math.PI * 2); ctx.stroke();
        };
        if (y1 >= -5 && y1 <= chartH + 5) drawDot(x1, y1, 6);
        if (y2 >= -5 && y2 <= chartH + 5) drawDot(x2, y2, 6);
        if (ym >= -5 && ym <= chartH + 5) drawDot(xm, ym, 5);

        // ── Status badge (bottom-right) ──────────────────────────────
        ctx.font = "bold 10px ui-sans-serif, system-ui, -apple-system";
        const tt = tLm(lang).patterns || {};
        const baseLabel = isTop
          ? (tt.label_double_top || "Doble Techo")
          : (tt.label_double_bottom || "Doble Suelo");
        let labelMain, labelSub;
        if (invalidated) {
          labelMain = `${baseLabel} · ${tt.invalidated_badge || "✗ INVALIDADO"}`;
          labelSub = isTop
            ? (tt.invalidated_reason_doubletop || "precio cerró por encima de los picos")
            : (tt.invalidated_reason_doublebottom || "precio cerró por debajo de los mínimos");
        } else {
          const statusWord = confirmed
            ? (tt.status_confirmed || "confirmado")
            : (tt.status_forming || "formándose");
          labelMain = `${baseLabel} · ${statusWord}`;
          labelSub = confirmed
            ? null
            : (tt.neckline_at || "Neckline en {price}").replace("{price}", "$" + fmtPrice(pRes.neckline));
        }
        const padX2 = 7;
        const mainW = ctx.measureText(labelMain).width;
        const subW = labelSub ? ctx.measureText(labelSub).width : 0;
        const boxW = Math.max(mainW, subW) + padX2 * 2;
        const boxH = labelSub ? 30 : 18;
        const bx = chartW - boxW - 8 - 36; // reserve 36px for the bottom-right "?" legend btn
        const by = chartH - boxH - 8;
        ctx.fillStyle = invalidated
          ? "rgba(8,5,8,0.92)"
          : confirmed
            ? "rgba(236,72,153,0.12)"
            : "rgba(8,5,8,0.85)";
        roundRect(ctx, bx, by, boxW, boxH, 4);
        ctx.fill();
        ctx.strokeStyle = invalidated ? PT_INV_COL : confirmed ? mainCol : dimCol;
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.fillStyle = invalidated ? PT_INV_COL : confirmed ? mainCol : dimCol;
        ctx.fillText(labelMain, bx + padX2, by + 12);
        if (labelSub) {
          ctx.font = "10px ui-sans-serif, system-ui, -apple-system";
          ctx.fillStyle = invalidated
            ? "rgba(156,163,175,0.75)"
            : "rgba(236,72,153,0.7)";
          ctx.fillText(labelSub, bx + padX2, by + 25);
        }
        ctx.restore();

        // Publish hit data so the tooltip handler can use it.
        patternsHitRef.current = {
          pattern: pRes.pattern, status: pRes.status,
          isTop, confirmed, invalidated,
          neckline: pRes.neckline,
          breakIdx: pRes.breakIdx, breakPrice: pRes.breakPrice,
          invalidationIdx: pRes.invalidationIdx, invalidationPrice: pRes.invalidationPrice,
          invalidationLevel: pRes.invalidationLevel,
          thresholds: pRes.thresholds,
          metrics: pRes.metrics,
          points: [
            { kind: isTop ? "peak1"  : "bottom1", x: x1, y: y1, p: a1.p, t: a1.t, i: a1.i },
            { kind: isTop ? "valley" : "ridge",   x: xm, y: ym, p: aM.p, t: aM.t, i: aM.i },
            { kind: isTop ? "peak2"  : "bottom2", x: x2, y: y2, p: a2.p, t: a2.t, i: a2.i },
          ],
          neckSeg: { x1, x2: xNeckEnd, y: yNeck },
        };
      } else if (pRes.pattern === "triangle") {
        // ── Triangle render — two converging trendlines + apex marker.
        // Reuses the same magenta palette as Double Top/Bottom to keep
        // "this is the patterns family" visually consistent; the shape
        // itself (two lines vs three anchors) tells the type apart.
        const PT_COL = "#ec4899";
        const PT_COL_DIM = "rgba(236,72,153,0.5)";
        const PT_INV_COL = "#9ca3af";
        const PT_INV_COL_DIM = "rgba(156,163,175,0.55)";
        const HALO = "rgba(255,255,255,0.40)";
        const status = pRes.status;
        const confirmed = status === "confirmed";
        const invalidated = status === "invalidated";
        const mainCol = invalidated ? PT_INV_COL : PT_COL;
        const dimCol  = invalidated ? PT_INV_COL_DIM : PT_COL_DIM;
        const n = candles.length;
        const visW = chartW;
        const step = visW / n;
        const xOfIdx = (idx) => idx * step + step / 2;
        const yOfLocal = (price) => {
          const min = viewRange.min, max = viewRange.max;
          return chartH - ((price - min) / (max - min || 1)) * chartH;
        };
        const yOnLine = (line, idx) => yOfLocal(line.m * idx + line.b);
        const xStart = xOfIdx(pRes.patternStartIdx);
        const xEnd = xOfIdx(Math.min(n - 1, pRes.patternEndIdx));
        const xExtend = (() => {
          if (pRes.apexIdx == null) return xEnd;
          const cap = Math.min(
            pRes.apexIdx,
            n - 1,
            pRes.patternEndIdx + (pRes.patternEndIdx - pRes.patternStartIdx) * 1.5
          );
          return xOfIdx(cap);
        })();
        const endIdx = pRes.apexIdx != null
          ? Math.min(pRes.apexIdx, n - 1, pRes.patternEndIdx + (pRes.patternEndIdx - pRes.patternStartIdx) * 1.5)
          : pRes.patternEndIdx;

        ctx.save();
        ctx.lineCap = "round"; ctx.lineJoin = "round";
        // Halo underlay (both lines).
        ctx.setLineDash([]);
        ctx.strokeStyle = HALO;
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.moveTo(xStart, yOnLine(pRes.highLine, pRes.patternStartIdx));
        ctx.lineTo(xExtend, yOnLine(pRes.highLine, endIdx));
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(xStart, yOnLine(pRes.lowLine, pRes.patternStartIdx));
        ctx.lineTo(xExtend, yOnLine(pRes.lowLine, endIdx));
        ctx.stroke();
        // Foreground.
        ctx.strokeStyle = confirmed ? mainCol : dimCol;
        ctx.lineWidth = confirmed ? 2.4 : invalidated ? 2.0 : 2.0;
        ctx.setLineDash(confirmed ? [] : invalidated ? [6, 4] : [5, 4]);
        // High (resistance) line
        ctx.beginPath();
        ctx.moveTo(xStart, yOnLine(pRes.highLine, pRes.patternStartIdx));
        ctx.lineTo(xExtend, yOnLine(pRes.highLine, endIdx));
        ctx.stroke();
        // Low (support) line
        ctx.beginPath();
        ctx.moveTo(xStart, yOnLine(pRes.lowLine, pRes.patternStartIdx));
        ctx.lineTo(xExtend, yOnLine(pRes.lowLine, endIdx));
        ctx.stroke();
        ctx.setLineDash([]);

        // Pivot dots — every swing high / low that belongs to the line.
        const pivotScreen = [];
        const drawDot = (x, y, r) => {
          ctx.save();
          ctx.shadowBlur = 6;
          ctx.shadowColor = "rgba(0,0,0,0.6)";
          ctx.fillStyle = mainCol;
          ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
          ctx.restore();
          ctx.strokeStyle = "rgba(255,255,255,0.92)";
          ctx.lineWidth = 1.2;
          ctx.beginPath(); ctx.arc(x, y, Math.max(1, r - 2), 0, Math.PI * 2); ctx.stroke();
        };
        for (const p of pRes.highLine.points) {
          const px = xOfIdx(p.i), py = yOfLocal(p.p);
          pivotScreen.push({ kind: "high", x: px, y: py, p: p.p, t: p.t, i: p.i });
          if (py >= -5 && py <= chartH + 5) drawDot(px, py, 5);
        }
        for (const p of pRes.lowLine.points) {
          const px = xOfIdx(p.i), py = yOfLocal(p.p);
          pivotScreen.push({ kind: "low", x: px, y: py, p: p.p, t: p.t, i: p.i });
          if (py >= -5 && py <= chartH + 5) drawDot(px, py, 5);
        }

        // Apex marker (tiny X where the lines would meet — only if it
        // falls inside the visible canvas).
        if (pRes.apexIdx != null && pRes.apexIdx > 0 && pRes.apexIdx < n) {
          const ax = xOfIdx(pRes.apexIdx);
          const ay = yOfLocal(pRes.highLine.m * pRes.apexIdx + pRes.highLine.b);
          ctx.strokeStyle = dimCol;
          ctx.lineWidth = 1.4;
          ctx.beginPath();
          ctx.moveTo(ax - 4, ay - 4); ctx.lineTo(ax + 4, ay + 4);
          ctx.moveTo(ax + 4, ay - 4); ctx.lineTo(ax - 4, ay + 4);
          ctx.stroke();
        }

        // Break marker (X) if confirmed.
        if (confirmed && pRes.breakIdx != null) {
          const xb = xOfIdx(pRes.breakIdx);
          const yb = yOfLocal(pRes.breakPrice);
          ctx.strokeStyle = mainCol;
          ctx.lineWidth = 2.4;
          ctx.beginPath();
          ctx.moveTo(xb - 5, yb - 5); ctx.lineTo(xb + 5, yb + 5);
          ctx.moveTo(xb + 5, yb - 5); ctx.lineTo(xb - 5, yb + 5);
          ctx.stroke();
        }
        // Invalidation marker (red X) if the break went the wrong way.
        if (invalidated && pRes.invalidationIdx != null) {
          const xi = xOfIdx(pRes.invalidationIdx);
          const yi = yOfLocal(pRes.invalidationPrice);
          ctx.strokeStyle = "#f87171";
          ctx.lineWidth = 2.4;
          ctx.beginPath();
          ctx.moveTo(xi - 5, yi - 5); ctx.lineTo(xi + 5, yi + 5);
          ctx.moveTo(xi + 5, yi - 5); ctx.lineTo(xi - 5, yi + 5);
          ctx.stroke();
        }

        // Badge — bottom-right.
        ctx.font = "bold 10px ui-sans-serif, system-ui, -apple-system";
        const tt = tLm(lang).patterns || {};
        const kindWord = pRes.kind === "ascending"
          ? (tt.triangle_ascending || "Triángulo Ascendente")
          : pRes.kind === "descending"
            ? (tt.triangle_descending || "Triángulo Descendente")
            : (tt.triangle_symmetric || "Triángulo Simétrico");
        let labelMain, labelSub;
        if (invalidated) {
          labelMain = `${kindWord} · ${tt.invalidated_badge || "✗ INVALIDADO"}`;
          labelSub = pRes.kind === "ascending"
            ? (tt.invalidated_reason_triangle_asc || "precio rompió a la baja")
            : (tt.invalidated_reason_triangle_desc || "precio rompió al alza");
        } else {
          const statusWord = confirmed
            ? (tt.status_confirmed || "confirmado")
            : (tt.status_forming || "formándose");
          labelMain = `${kindWord} · ${statusWord}`;
          labelSub = confirmed ? null : (tt.pending_break_triangle || "vértice pendiente");
        }
        const padX2 = 7;
        const mainW = ctx.measureText(labelMain).width;
        const subW = labelSub ? ctx.measureText(labelSub).width : 0;
        const boxW = Math.max(mainW, subW) + padX2 * 2;
        const boxH = labelSub ? 30 : 18;
        const bx = chartW - boxW - 8 - 36; // reserve 36px for the bottom-right "?" legend btn
        const by = chartH - boxH - 8;
        ctx.fillStyle = invalidated
          ? "rgba(8,5,8,0.92)"
          : confirmed
            ? "rgba(236,72,153,0.12)"
            : "rgba(8,5,8,0.85)";
        roundRect(ctx, bx, by, boxW, boxH, 4);
        ctx.fill();
        ctx.strokeStyle = invalidated ? PT_INV_COL : confirmed ? mainCol : dimCol;
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.fillStyle = invalidated ? PT_INV_COL : confirmed ? mainCol : dimCol;
        ctx.fillText(labelMain, bx + padX2, by + 12);
        if (labelSub) {
          ctx.font = "10px ui-sans-serif, system-ui, -apple-system";
          ctx.fillStyle = invalidated
            ? "rgba(156,163,175,0.75)"
            : "rgba(236,72,153,0.7)";
          ctx.fillText(labelSub, bx + padX2, by + 25);
        }
        ctx.restore();

        // Publish hit data — used by the same tooltip handler. The
        // tooltip code checks `triangleData` to branch its layout.
        patternsHitRef.current = {
          pattern: "triangle",
          status: pRes.status,
          isTriangle: true,
          kind: pRes.kind,
          confirmed, invalidated,
          breakIdx: pRes.breakIdx, breakPrice: pRes.breakPrice, breakDir: pRes.breakDir,
          invalidationIdx: pRes.invalidationIdx, invalidationPrice: pRes.invalidationPrice,
          apexIdx: pRes.apexIdx,
          thresholds: pRes.thresholds,
          points: pivotScreen,
          highLine: pRes.highLine, lowLine: pRes.lowLine,
          // Lines for hit detection (in screen coords).
          highLineSeg: { x1: xStart, x2: xExtend, m: pRes.highLine.m, b: pRes.highLine.b },
          lowLineSeg:  { x1: xStart, x2: xExtend, m: pRes.lowLine.m,  b: pRes.lowLine.b },
        };
      } else if (pRes.pattern === "hs" || pRes.pattern === "inv_hs") {
        // ── Head & Shoulders / Inverse H&S render ────────────────
        // Same magenta family (#ec4899) — visually consistent with the
        // other chartist patterns. Geometry: 3 anchor dots (LS, H, RS)
        // + DIAGONAL neckline from neck1→neck2 (own slope), solid if
        // confirmed (break detected), dashed if forming.
        const PT_COL = "#ec4899";
        const PT_COL_DIM = "rgba(236,72,153,0.5)";
        const PT_INV_COL = "#9ca3af";
        const PT_INV_COL_DIM = "rgba(156,163,175,0.55)";
        const HALO = "rgba(255,255,255,0.40)";
        const isHS = pRes.pattern === "hs";
        const status = pRes.status;
        const confirmed = status === "confirmed";
        const invalidated = status === "invalidated";
        const mainCol = invalidated ? PT_INV_COL : PT_COL;
        const dimCol  = invalidated ? PT_INV_COL_DIM : PT_COL_DIM;
        const n = candles.length;
        const step = chartW / n;
        const xOfIdx = (idx) => idx * step + step / 2;
        const yOfLocal = (price) => {
          const min = viewRange.min, max = viewRange.max;
          return chartH - ((price - min) / (max - min || 1)) * chartH;
        };
        const xLS = xOfIdx(pRes.LS.i), yLS = yOfLocal(pRes.LS.p);
        const xH  = xOfIdx(pRes.H.i),  yH  = yOfLocal(pRes.H.p);
        const xRS = xOfIdx(pRes.RS.i), yRS = yOfLocal(pRes.RS.p);
        const xN1 = xOfIdx(pRes.neck1.i), yN1 = yOfLocal(pRes.neck1.p);
        const xN2 = xOfIdx(pRes.neck2.i), yN2 = yOfLocal(pRes.neck2.p);

        ctx.save();
        ctx.lineCap = "round"; ctx.lineJoin = "round";
        // Neckline diagonal. Halo + foreground, dashed for forming/invalidated.
        const xExtend = Math.min(chartW - 4, xN2 + step * 8);
        const yExtend = yOfLocal(pRes.neckline.m * ((xExtend - step / 2) / step) + pRes.neckline.b);
        ctx.setLineDash([]);
        ctx.strokeStyle = HALO;
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.moveTo(xN1, yN1); ctx.lineTo(xExtend, yExtend);
        ctx.stroke();
        ctx.strokeStyle = confirmed ? mainCol : dimCol;
        ctx.lineWidth = confirmed ? 2.4 : 2.0;
        ctx.setLineDash(confirmed ? [] : invalidated ? [6, 4] : [5, 4]);
        ctx.beginPath();
        ctx.moveTo(xN1, yN1); ctx.lineTo(xExtend, yExtend);
        ctx.stroke();
        ctx.setLineDash([]);

        // Connector polyline LS → H → RS (light, to read the geometry).
        ctx.strokeStyle = dimCol;
        ctx.lineWidth = 1.2;
        ctx.setLineDash([2, 3]);
        ctx.beginPath();
        ctx.moveTo(xLS, yLS); ctx.lineTo(xH, yH); ctx.lineTo(xRS, yRS);
        ctx.stroke();
        ctx.setLineDash([]);

        // Break marker (X) if confirmed.
        if (confirmed && pRes.breakIdx != null) {
          const xb = xOfIdx(pRes.breakIdx);
          const yb = yOfLocal(pRes.breakPrice);
          ctx.strokeStyle = mainCol;
          ctx.lineWidth = 2.4;
          ctx.beginPath();
          ctx.moveTo(xb - 5, yb - 5); ctx.lineTo(xb + 5, yb + 5);
          ctx.moveTo(xb + 5, yb - 5); ctx.lineTo(xb - 5, yb + 5);
          ctx.stroke();
        }
        // Invalidation marker (red X) if the head was breached the wrong way.
        if (invalidated && pRes.invalidationIdx != null) {
          const xi = xOfIdx(pRes.invalidationIdx);
          const yi = yOfLocal(pRes.invalidationPrice);
          ctx.strokeStyle = "#f87171";
          ctx.lineWidth = 2.4;
          ctx.beginPath();
          ctx.moveTo(xi - 5, yi - 5); ctx.lineTo(xi + 5, yi + 5);
          ctx.moveTo(xi + 5, yi - 5); ctx.lineTo(xi - 5, yi + 5);
          ctx.stroke();
        }

        // Anchor dots — Head bigger to read prominence; shadow + halo
        // ring per Punto D for contrast against heatmap.
        const drawDot = (x, y, r) => {
          ctx.save();
          ctx.shadowBlur = 6;
          ctx.shadowColor = "rgba(0,0,0,0.6)";
          ctx.fillStyle = mainCol;
          ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
          ctx.restore();
          ctx.strokeStyle = "rgba(255,255,255,0.92)";
          ctx.lineWidth = 1.2;
          ctx.beginPath(); ctx.arc(x, y, Math.max(1, r - 2), 0, Math.PI * 2); ctx.stroke();
        };
        if (yLS >= -5 && yLS <= chartH + 5) drawDot(xLS, yLS, 6.0);
        if (yH  >= -5 && yH  <= chartH + 5) drawDot(xH,  yH,  7.0);   // head = bigger
        if (yRS >= -5 && yRS <= chartH + 5) drawDot(xRS, yRS, 6.0);

        // Badge — bottom-right.
        ctx.font = "bold 10px ui-sans-serif, system-ui, -apple-system";
        const tt = tLm(lang).patterns || {};
        const kindWord = isHS
          ? (tt.label_hs || "Hombro-Cabeza-Hombro")
          : (tt.label_inv_hs || "HCH Invertido");
        let labelMain, labelSub;
        if (invalidated) {
          labelMain = `${kindWord} · ${tt.invalidated_badge || "✗ INVALIDADO"}`;
          labelSub = isHS
            ? (tt.invalidated_reason_hs || "precio cerró por encima de la cabeza")
            : (tt.invalidated_reason_inv_hs || "precio cerró por debajo de la cabeza");
        } else {
          const statusWord = confirmed
            ? (tt.status_confirmed || "confirmado")
            : (tt.status_forming || "formándose");
          labelMain = `${kindWord} · ${statusWord}`;
          labelSub = confirmed ? null : (tt.pending_break || "falta romper neckline");
        }
        const padX2 = 7;
        const mainW = ctx.measureText(labelMain).width;
        const subW = labelSub ? ctx.measureText(labelSub).width : 0;
        const boxW = Math.max(mainW, subW) + padX2 * 2;
        const boxH = labelSub ? 30 : 18;
        const bx = chartW - boxW - 8 - 36; // reserve 36px for the bottom-right "?" legend btn
        const by = chartH - boxH - 8;
        ctx.fillStyle = invalidated
          ? "rgba(8,5,8,0.92)"
          : confirmed
            ? "rgba(236,72,153,0.12)"
            : "rgba(8,5,8,0.85)";
        roundRect(ctx, bx, by, boxW, boxH, 4);
        ctx.fill();
        ctx.strokeStyle = invalidated ? PT_INV_COL : confirmed ? mainCol : dimCol;
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.fillStyle = invalidated ? PT_INV_COL : confirmed ? mainCol : dimCol;
        ctx.fillText(labelMain, bx + padX2, by + 12);
        if (labelSub) {
          ctx.font = "10px ui-sans-serif, system-ui, -apple-system";
          ctx.fillStyle = invalidated
            ? "rgba(156,163,175,0.75)"
            : "rgba(236,72,153,0.7)";
          ctx.fillText(labelSub, bx + padX2, by + 25);
        }
        ctx.restore();

        // Publish hit data for the tooltip handler. Reuses the same
        // `points` shape used by Double Top/Bottom so the existing hit
        // loop catches LS/H/RS without modification. A separate flag
        // `isHCH` tells the tooltip to render the HCH-specific layout.
        patternsHitRef.current = {
          pattern: pRes.pattern,
          status: pRes.status,
          isHCH: true,
          isInverse: !isHS,
          confirmed, invalidated,
          neckline: pRes.neckline,
          breakIdx: pRes.breakIdx, breakPrice: pRes.breakPrice,
          invalidationIdx: pRes.invalidationIdx, invalidationPrice: pRes.invalidationPrice,
          invalidationLevel: pRes.invalidationLevel,
          thresholds: pRes.thresholds,
          metrics: pRes.metrics,
          points: [
            { kind: "ls",   x: xLS, y: yLS, p: pRes.LS.p, t: pRes.LS.t, i: pRes.LS.i },
            { kind: "head", x: xH,  y: yH,  p: pRes.H.p,  t: pRes.H.t,  i: pRes.H.i  },
            { kind: "rs",   x: xRS, y: yRS, p: pRes.RS.p, t: pRes.RS.t, i: pRes.RS.i },
          ],
          // Neckline segment in screen coords for line hit-detection.
          necklineSeg: { x1: xN1, y1: yN1, x2: xExtend, y2: yExtend, m: pRes.neckline.m, b: pRes.neckline.b },
        };
      } else {
        // No pattern — discrete bottom-right note.
        patternsHitRef.current = null;
        ctx.save();
        ctx.font = "bold 10px ui-sans-serif, system-ui, -apple-system";
        const tt = tLm(lang).patterns || {};
        const msg = tt.no_pattern || "Sin patrón detectado";
        const msgW = ctx.measureText(msg).width;
        const padX = 6;
        const mbx = chartW - msgW - padX * 2 - 8 - 36; // reserve 36px for "?" legend btn at bottom-right
        const mby = chartH - 22;
        ctx.fillStyle = "rgba(8,5,8,0.85)";
        roundRect(ctx, mbx, mby, msgW + padX * 2, 16, 4);
        ctx.fill();
        ctx.strokeStyle = "rgba(236,72,153,0.5)";
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.fillStyle = "rgba(236,72,153,0.85)";
        ctx.fillText(msg, mbx + padX, mby + 12);
        ctx.restore();
      }
    }

    // ── Candlestick patterns layer (independent toggle) ──────────────
    // Magenta family is reserved for chartist patterns. Candles use
    // green/red/gray semaphore: green = bullish, red = bearish,
    // gray = neutral (doji). Marker is a 6 px outlined circle placed
    // BELOW low for bullish (support from below) / ABOVE high for
    // bearish (rejection from above). Neutral (doji) sits above the
    // high as well. Each hit is also pushed to `candlesHitRef` so the
    // tooltip handler can show pattern type / signal / body ratio on
    // hover or tap.
    if (!showCandles) {
      candlesHitRef.current = [];
    }
    if (showCandles && candles.length) {
      const cRes = candlePatterns;
      const n = candles.length;
      const step = chartW / n;
      const xOfIdx = (idx) => idx * step + step / 2;
      const yOfLocal = (price) => {
        const min = viewRange.min, max = viewRange.max;
        return chartH - ((price - min) / (max - min || 1)) * chartH;
      };
      const colFor = (sig) => sig === "bullish" ? "#22c55e" : sig === "bearish" ? "#ef4444" : "#9ca3af";

      // Phase 1: compute screen positions for every detected pattern,
      // then group markers whose centres lie within CLUSTER_R px of
      // each other. Each group is rendered as a single neutral counter
      // badge "●N"; isolated markers get their type-specific shape.
      const CLUSTER_R = 14;
      const raw = [];
      for (const it of cRes) {
        if (it.i < 0 || it.i >= n) continue;
        const cd = candles[it.i];
        if (!cd) continue;
        const cx = xOfIdx(it.i);
        const cy = it.signal === "bullish"
          ? Math.min(chartH - 6, yOfLocal(cd.l) + 10)
          : Math.max(6, yOfLocal(cd.h) - 10);
        if (cy < -6 || cy > chartH + 6) continue;
        raw.push({ i: it.i, x: cx, y: cy, pattern: it.pattern, signal: it.signal,
                   metrics: it.metrics, thresholds: it.thresholds, context: it.context,
                   t: cd.t, p: cd.c });
      }
      // Greedy single-link clustering — each raw point joins the first
      // existing group within CLUSTER_R, otherwise starts a new one.
      const groups = [];
      for (const p of raw) {
        let joined = null;
        for (const g of groups) {
          if (Math.hypot(p.x - g.cx, p.y - g.cy) <= CLUSTER_R) { joined = g; break; }
        }
        if (joined) {
          joined.points.push(p);
          joined.cx = joined.points.reduce((s, q) => s + q.x, 0) / joined.points.length;
          joined.cy = joined.points.reduce((s, q) => s + q.y, 0) / joined.points.length;
        } else {
          groups.push({ cx: p.x, cy: p.y, points: [p] });
        }
      }

      ctx.save();
      // Shape helpers — each draws inside a 9×9 px bounding box centred
      // at (cx, cy). All carry a dark halo (shadowBlur=8) for contrast
      // against any heatmap colour, then a 1.2 px white stroke on top.
      const haloAndStroke = () => {
        ctx.shadowBlur = 0;
        ctx.strokeStyle = "rgba(255,255,255,0.92)";
        ctx.lineWidth = 1.2;
      };
      const startShape = (col) => {
        ctx.save();
        ctx.shadowBlur = 8;
        ctx.shadowColor = "rgba(0,0,0,0.65)";
        ctx.fillStyle = col;
      };
      const drawDoji = (cx, cy, col) => {
        // ✕ — two diagonal lines, 9 px reach.
        startShape(col);
        ctx.strokeStyle = col;
        ctx.lineWidth = 2.2;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(cx - 4, cy - 4); ctx.lineTo(cx + 4, cy + 4);
        ctx.moveTo(cx + 4, cy - 4); ctx.lineTo(cx - 4, cy + 4);
        ctx.stroke();
        ctx.restore();
        // White outline on top for legibility on dark+bright heatmap mix.
        ctx.save();
        ctx.strokeStyle = "rgba(255,255,255,0.55)";
        ctx.lineWidth = 0.6;
        ctx.beginPath();
        ctx.moveTo(cx - 4, cy - 4); ctx.lineTo(cx + 4, cy + 4);
        ctx.moveTo(cx + 4, cy - 4); ctx.lineTo(cx - 4, cy + 4);
        ctx.stroke();
        ctx.restore();
      };
      const drawTriangle = (cx, cy, col, down) => {
        // ▼/▲ — equilateral triangle base 10 px.
        startShape(col);
        ctx.beginPath();
        if (down) {
          ctx.moveTo(cx - 5, cy - 4);
          ctx.lineTo(cx + 5, cy - 4);
          ctx.lineTo(cx, cy + 5);
        } else {
          ctx.moveTo(cx - 5, cy + 4);
          ctx.lineTo(cx + 5, cy + 4);
          ctx.lineTo(cx, cy - 5);
        }
        ctx.closePath();
        ctx.fill();
        ctx.restore();
        haloAndStroke();
        ctx.beginPath();
        if (down) {
          ctx.moveTo(cx - 5, cy - 4);
          ctx.lineTo(cx + 5, cy - 4);
          ctx.lineTo(cx, cy + 5);
        } else {
          ctx.moveTo(cx - 5, cy + 4);
          ctx.lineTo(cx + 5, cy + 4);
          ctx.lineTo(cx, cy - 5);
        }
        ctx.closePath();
        ctx.stroke();
      };
      const drawDiamond = (cx, cy, col) => {
        // ◆ — square rotated 45°, side ≈ 9 px.
        startShape(col);
        ctx.beginPath();
        ctx.moveTo(cx, cy - 5);
        ctx.lineTo(cx + 5, cy);
        ctx.lineTo(cx, cy + 5);
        ctx.lineTo(cx - 5, cy);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
        haloAndStroke();
        ctx.beginPath();
        ctx.moveTo(cx, cy - 5);
        ctx.lineTo(cx + 5, cy);
        ctx.lineTo(cx, cy + 5);
        ctx.lineTo(cx - 5, cy);
        ctx.closePath();
        ctx.stroke();
      };
      const drawClusterBadge = (cx, cy, count) => {
        startShape("#9ca3af");
        ctx.beginPath(); ctx.arc(cx, cy, 9, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
        haloAndStroke();
        ctx.beginPath(); ctx.arc(cx, cy, 9, 0, Math.PI * 2); ctx.stroke();
        ctx.fillStyle = "#0b0b10";
        ctx.font = "bold 10px ui-sans-serif, system-ui, -apple-system";
        ctx.textAlign = "center"; ctx.textBaseline = "middle";
        ctx.fillText(`${count}`, cx, cy + 0.5);
        ctx.textAlign = "start"; ctx.textBaseline = "alphabetic";
      };
      const shapeFor = (pattern, cx, cy, col) => {
        if (pattern === "doji") return drawDoji(cx, cy, col);
        if (pattern === "hammer" || pattern === "hanging_man") return drawTriangle(cx, cy, col, true);
        if (pattern === "inverted_hammer" || pattern === "shooting_star") return drawTriangle(cx, cy, col, false);
        if (pattern === "engulfing_bull" || pattern === "engulfing_bear") return drawDiamond(cx, cy, col);
        // Fallback: small filled circle (should not happen).
        startShape(col);
        ctx.beginPath(); ctx.arc(cx, cy, 5, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
      };

      // Render: groups of 1 → typed shape; groups of ≥2 → cluster badge.
      const hits = [];
      for (const g of groups) {
        if (g.points.length === 1) {
          const p = g.points[0];
          shapeFor(p.pattern, p.x, p.y, colFor(p.signal));
          hits.push(p);
        } else {
          drawClusterBadge(g.cx, g.cy, g.points.length);
          // Cluster hover: tooltip lists ALL members. Encode the group
          // via `clusterMembers` so the hit-detection picks any member
          // when within radius and the tooltip can render the list.
          hits.push({
            i: g.points[0].i, x: g.cx, y: g.cy,
            pattern: "_cluster", signal: "neutral",
            clusterMembers: g.points,
            metrics: { count: g.points.length },
            thresholds: g.points[0].thresholds || {},
            context: g.points[0].context || {},
            t: g.points[0].t, p: g.points[0].p,
          });
        }
      }
      ctx.restore();
      candlesHitRef.current = hits;

      // Counter badge in the bottom-left is REMOVED — the legend (DOM
      // element rendered by the wrapper) now owns the total count. See
      // the candle-legend block at the bottom of the component.
    }
    return undefined;
    };
    srRedrawRef.current = redraw;
    redraw();
  }, [srData, showRange, showTrendline, showPatterns, showCandles, coin, lang, candles, candlePatterns, viewRange.min, viewRange.max]);

  // ─── Drawing engine — render effect (Fase B1) ─────────────────────
  // Redraws the dedicated `drawRef` canvas whenever the drawings list,
  // active draft, viewRange, candle count, eraser hover, or chart size
  // change. DPR transform is reset every frame so resizing stays crisp.
  // Wrapped in `redraw` closure stored in `drawCanvasRedrawRef` so the
  // central ResizeObserver can re-invoke it on container size changes.
  useEffect(() => {
    const redraw = () => {
      const cnv = drawRef.current;
      if (!cnv) return;
      const ctx = cnv.getContext("2d");
      if (!ctx) return;
      const dpr = window.devicePixelRatio || 1;
      const r = cnv.getBoundingClientRect();
      if (!r.width || !r.height) return;
      cnv.width = r.width * dpr;
      cnv.height = r.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const chartW = r.width - RIGHT_AXIS;
      const chartH = r.height - BOTTOM_AXIS;
      const nCandles = candles.length || 1;
      const ctxCoords = { viewRange, chartW, chartH, nCandles };
      const eraserPointer = (showDrawingTool && activeTool === "eraser") ? eraserHoverRef.current : null;
      // Global HIDE: skip rendering of all permanent drawings AND the
      // eraser hover halo (the draft is still shown so the user gets
      // feedback during the trace, but no committed drawing leaks).
      if (allDrawingsHidden) {
        ctx.clearRect(0, 0, r.width, r.height);
        if (draftRef.current) {
          drawingEngine.renderAll(ctx, [], draftRef.current, null, ctxCoords);
        }
        return;
      }
      if (!showDrawingTool || (!drawings.length && !draftRef.current && !eraserPointer)) {
        ctx.clearRect(0, 0, r.width, r.height);
        return;
      }
      drawingEngine.renderAll(ctx, drawings || [], draftRef.current, eraserPointer, ctxCoords, contextMenu?.drawingId);
    };
    drawCanvasRedrawRef.current = redraw;
    redraw();
  }, [showDrawingTool, activeTool, drawings, draftRev, candles.length, viewRange.min, viewRange.max, allDrawingsHidden, contextMenu]);

  // ─── Drawing engine — cancel any in-flight draft when the user
  //     switches tool, or presses Esc. Channel/Position drafts need this
  //     explicit cleanup because they don't auto-finalize on pointerup. ───
  useEffect(() => {
    draftRef.current = null;
    eraserHoverRef.current = null;
    setDraftRev((v) => v + 1);
  }, [activeTool]);
  useEffect(() => {
    if (!showDrawingTool) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (draftRef.current) {
          draftRef.current = null;
          setDraftRev((v) => v + 1);
        }
      }
      // Polyline / Forecast: Enter finalizes the current draft.
      if (e.key === "Enter" && draftRef.current
          && (draftRef.current.type === "polyline" || draftRef.current.type === "forecast")) {
        const fin = drawingEngine.finalizeDraft(draftRef.current, candles.length || 1);
        draftRef.current = null;
        setDraftRev((v) => v + 1);
        if (fin && onDrawingsChange) {
          onDrawingsChange([...drawings, fin]);
          if (!persistentMode && onAutoRevertTool) onAutoRevertTool();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showDrawingTool, drawings, onDrawingsChange, candles.length, persistentMode, onAutoRevertTool]);

  // Polyline / Forecast finalize via double-click on the drawing canvas.
  const onDoubleClick = () => {
    if (!showDrawingTool) return;
    if (activeTool !== "polyline" && activeTool !== "forecast") return;
    if (!draftRef.current
        || (draftRef.current.type !== "polyline" && draftRef.current.type !== "forecast")) return;
    const fin = drawingEngine.finalizeDraft(draftRef.current, candles.length || 1);
    draftRef.current = null;
    setDraftRev((v) => v + 1);
    if (fin && onDrawingsChange) {
      onDrawingsChange([...drawings, fin]);
      if (!persistentMode && onAutoRevertTool) onAutoRevertTool();
    }
  };

  // ── B3b — Right-click contextual menu ──────────────────────────────
  // preventDefault is gated by `showDrawingTool` so users keep the native
  // browser menu when the drawing layer is off. If a multi-step draft is
  // in flight, right-click CANCELS that draft (parity with Esc) instead
  // of opening the menu — prevents accidental commits when the user
  // changes their mind mid-trace. Otherwise we hit-test the position
  // with the SAME function used by the eraser (single source of truth)
  // and only open the menu when a drawing is actually under the cursor.
  const onContextMenu = (e) => {
    if (!showDrawingTool) return;
    e.preventDefault();
    if (draftRef.current) {
      draftRef.current = null;
      setDraftRev((v) => v + 1);
      return;
    }
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const chartW = rect.width - RIGHT_AXIS;
    const chartH = rect.height - BOTTOM_AXIS;
    if (px < 0 || px > chartW || py < 0 || py > chartH) return;
    const ctxCoords = { viewRange, chartW, chartH, nCandles: candles.length || 1 };
    const hit = drawingEngine.hitTest(drawings, px, py, ctxCoords);
    if (hit) setContextMenu({ x: e.clientX, y: e.clientY, drawingId: hit.id });
  };

  // Close the contextual menu on outside-click. We rely on capture phase
  // so the listener fires before the menu's own onClick (otherwise click
  // INSIDE the menu would also close it before the action ran). Action
  // buttons call `closeContextMenu()` explicitly after mutating state.
  useEffect(() => {
    if (!contextMenu) return undefined;
    const handler = (ev) => {
      const root = document.getElementById(`liqmap-ctx-menu-${contextMenu.drawingId}`);
      if (root && root.contains(ev.target)) return;
      setContextMenu(null);
    };
    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, [contextMenu]);
  // Also close on Esc (keydown listener already exists for draft-cancel
  // — extend it). When the menu is open, Esc closes it; otherwise Esc
  // continues to cancel the in-flight draft as before.
  useEffect(() => {
    if (!contextMenu) return undefined;
    const onKey = (ev) => { if (ev.key === "Escape") setContextMenu(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [contextMenu]);

  // Helpers used by the menu action buttons. Each closes the menu after
  // mutating `drawings` via the parent's setter — keeps state ownership
  // intact (HeatmapCanvas does not own `drawings`, only the parent does).
  const closeContextMenu = () => setContextMenu(null);
  const ctxMenuTarget = contextMenu ? drawings.find((d) => d.id === contextMenu.drawingId) : null;
  const ctxToggleLock = () => {
    if (!ctxMenuTarget || !onDrawingsChange) return;
    onDrawingsChange(drawings.map((d) => d.id === ctxMenuTarget.id ? { ...d, locked: !d.locked } : d));
    closeContextMenu();
  };
  const ctxToggleHide = () => {
    if (!ctxMenuTarget || !onDrawingsChange) return;
    onDrawingsChange(drawings.map((d) => d.id === ctxMenuTarget.id ? { ...d, hidden: !d.hidden } : d));
    closeContextMenu();
  };
  const ctxClone = () => {
    if (!ctxMenuTarget || !onDrawingsChange) return;
    const clone = drawingEngine.cloneDrawing(ctxMenuTarget, viewRange, candles.length || 1);
    if (clone) onDrawingsChange([...drawings, clone]);
    closeContextMenu();
  };
  const ctxDelete = () => {
    // Locked items can still be deleted via Delete? Spec: "no permitir
    // editar/borrar ese dibujo vía eraser ni vía el propio menú (excepto
    // Desbloquear)". So Delete must be a no-op when target is locked.
    if (!ctxMenuTarget || !onDrawingsChange) return;
    if (ctxMenuTarget.locked) { closeContextMenu(); return; }
    onDrawingsChange(drawings.filter((d) => d.id !== ctxMenuTarget.id));
    closeContextMenu();
  };

  // ── B7b — Color + Grosor mutators ──────────────────────────────────
  // Both set the per-drawing field directly. Setting `color` also sets
  // `colorIsCustom: true` so the fib palette (drawingEngine.fibColor)
  // uniformly honors the user-chosen color across all fib-family levels.
  // Both mutators do NOT close the menu so the user can keep tweaking
  // (the menu has its own outside-click + Escape listeners).
  const ctxSetColor = (newColor) => {
    if (!ctxMenuTarget || !onDrawingsChange || !newColor) return;
    if (ctxMenuTarget.locked) return; // mirror Delete: locked = no-op
    onDrawingsChange(drawings.map((d) => d.id === ctxMenuTarget.id
      ? { ...d, color: newColor, colorIsCustom: true } : d));
  };
  const ctxSetWidth = (newWidth) => {
    if (!ctxMenuTarget || !onDrawingsChange || !newWidth) return;
    if (ctxMenuTarget.locked) return;
    if (drawingEngine.isStyleWidthLocked(ctxMenuTarget)) return; // highlighter
    onDrawingsChange(drawings.map((d) => d.id === ctxMenuTarget.id
      ? { ...d, width: newWidth } : d));
  };
  // ── B7b+ — Apply current style to ALL drawings of the same type ────
  // Bulk-equalizer: copies the active drawing's color/width/colorIsCustom
  // onto every OTHER drawing that shares the same `type`. Skips locked
  // drawings (their lock blocks all mutations). For highlighter targets
  // we deliberately do NOT propagate width — highlighter's width is
  // fixed by design (see isStyleWidthLocked). Closes the menu since the
  // operation is a one-shot commit and the user is unlikely to want to
  // keep tweaking after a bulk apply.
  const ctxApplyStyleToType = () => {
    if (!ctxMenuTarget || !onDrawingsChange) return;
    if (ctxMenuTarget.locked) return;
    const srcType = ctxMenuTarget.type;
    const srcColor = ctxMenuTarget.color;
    const srcWidth = ctxMenuTarget.width;
    const srcCustom = !!ctxMenuTarget.colorIsCustom;
    const propagateWidth = !drawingEngine.isStyleWidthLocked(ctxMenuTarget);
    onDrawingsChange(drawings.map((d) => {
      if (d.id === ctxMenuTarget.id) return d;     // leave source as-is
      if (d.type !== srcType) return d;            // different type
      if (d.locked) return d;                       // honor lock
      const next = { ...d };
      if (srcColor !== undefined) next.color = srcColor;
      if (srcCustom) next.colorIsCustom = true;
      if (propagateWidth && srcWidth !== undefined) next.width = srcWidth;
      return next;
    }));
    closeContextMenu();
  };

  return (
    <div
      ref={rootRef}
      className="absolute inset-0"
      data-liqmap-fullscreen-root="1"
      style={{
        touchAction: "none",
        overscrollBehavior: "contain",
        // In fullscreen mode the element becomes the top-level viewport; we
        // override the `absolute inset-0` positioning so it actually fills.
        // When an external fullscreen target is provided (parent's chart
        // container), the parent handles the viewport sizing and this ref
        // stays as a normal absolute child — do NOT override its position.
        ...(isFullscreen && !fullscreenTargetRef ? { position: "fixed", inset: 0, background: "#080508" } : {}),
      }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseLeave}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onContextMenu={onContextMenu}
      data-testid="liqmap-heatmap-root"
    >
      <style>{`
        /* ────────────────────────────────────────────────────────────
           Fullscreen styling — three INDEPENDENT rules per target,
           one per pseudo-class variant.

           Why split: per CSS Selectors Level 3/4 error-handling, a
           comma-separated selector list is invalidated AS A WHOLE if
           any one selector fails to parse. Vendor-prefixed pseudos
           (:-webkit-full-screen, :-moz-full-screen) are commonly
           treated as invalid by browsers that don't recognize them,
           which historically dropped the entire rule — leaving the
           container without display:flex/etc. and producing the
           "fullscreen at half screen" symptom across Chrome, Safari
           and Firefox alike. Independent rules guarantee that each
           engine keeps the variant it understands.
           ──────────────────────────────────────────────────────────── */

        /* Rama A: standalone heatmap root as the fullscreen target
           (used when no external fullscreenTargetRef is provided). */
        [data-liqmap-fullscreen-root]:fullscreen {
          width: 100vw;
          height: 100vh;
          background: #080508;
        }
        [data-liqmap-fullscreen-root]:-webkit-full-screen {
          width: 100vw;
          height: 100vh;
          background: #080508;
        }
        [data-liqmap-fullscreen-root]:-moz-full-screen {
          width: 100vw;
          height: 100vh;
          background: #080508;
        }

        /* Rama B: parent chart container is the fullscreen target.
           The sticky toolbar stays pinned at the top and the canvas
           wrap grows to fill the remaining height. */
        [data-liqmap-fs-container]:fullscreen {
          width: 100vw;
          height: 100vh;
          background: #080508;
          display: flex;
          flex-direction: column;
          padding: 8px 12px 0 12px;
          box-sizing: border-box;
        }
        [data-liqmap-fs-container]:-webkit-full-screen {
          width: 100vw;
          height: 100vh;
          background: #080508;
          display: flex;
          flex-direction: column;
          padding: 8px 12px 0 12px;
          box-sizing: border-box;
        }
        [data-liqmap-fs-container]:-moz-full-screen {
          width: 100vw;
          height: 100vh;
          background: #080508;
          display: flex;
          flex-direction: column;
          padding: 8px 12px 0 12px;
          box-sizing: border-box;
        }

        /* Canvas-wrap override (Rama B): canvas-wrap is a direct child
           of the flex-column chart container — make it grow to fill
           the remaining height instead of clinging to h-[58vh]. This
           is the rule that effectively makes the chart fill the
           fullscreen viewport; splitting it per-prefix is critical. */
        [data-liqmap-fs-container]:fullscreen [data-testid="liqmap-canvas-wrap"] {
          flex: 1 1 auto !important;
          height: auto !important;
          min-height: 0 !important;
        }
        [data-liqmap-fs-container]:-webkit-full-screen [data-testid="liqmap-canvas-wrap"] {
          flex: 1 1 auto !important;
          height: auto !important;
          min-height: 0 !important;
        }
        [data-liqmap-fs-container]:-moz-full-screen [data-testid="liqmap-canvas-wrap"] {
          flex: 1 1 auto !important;
          height: auto !important;
          min-height: 0 !important;
        }
      `}</style>
      <canvas
        ref={ref}
        className="absolute inset-0 h-full w-full"
        data-testid="liqmap-heatmap-canvas"
        style={{ display: "block", pointerEvents: "none" }}
      />
      <canvas
        ref={overlayRef}
        className="pointer-events-none absolute inset-0 h-full w-full"
        data-testid="liqmap-heatmap-overlay"
        style={{ display: "block" }}
      />
      <canvas
        ref={crossRef}
        className="pointer-events-none absolute inset-0 h-full w-full"
        data-testid="liqmap-heatmap-crosshair"
        style={{ display: "block" }}
      />
      <canvas
        ref={simRef}
        className="pointer-events-none absolute inset-0 h-full w-full"
        data-testid="liqmap-sim-overlay"
        style={{ display: "block" }}
      />
      <canvas
        ref={whaleRef}
        className="pointer-events-none absolute inset-0 h-full w-full"
        data-testid="liqmap-whale-overlay"
        style={{ display: "block" }}
      />
      {/* EMA overlay (Layer 5.5) — 4 EMA lines + cross markers. Sits
          ABOVE ballenas, BELOW S/R IA / Patterns (analytic signals win
          visual prominence over derived indicators). */}
      <canvas
        ref={emaRef}
        className="pointer-events-none absolute inset-0 h-full w-full"
        data-testid="liqmap-ema-overlay"
        style={{ display: "block" }}
      />
      <canvas
        ref={srRef}
        className="pointer-events-none absolute inset-0 h-full w-full"
        data-testid="liqmap-sr-overlay"
        style={{ display: "block" }}
      />
      {/* Drawing engine canvas (Fase B1) — sits above the analytic overlays
          and below the DOM tooltips. pointerEvents flip to 'auto' when the
          drawing tool is on AND the active tool is not the passive cursor,
          so pan/zoom keeps working in cursor mode. */}
      <canvas
        ref={drawRef}
        className="absolute inset-0 h-full w-full"
        data-testid="liqmap-drawing-canvas"
        onDoubleClick={onDoubleClick}
        style={{
          display: "block",
          pointerEvents: (showDrawingTool && activeTool !== "cursor") ? "auto" : "none",
          cursor: (showDrawingTool && activeTool === "eraser") ? "not-allowed"
                : (showDrawingTool && activeTool !== "cursor") ? "crosshair"
                : "default",
        }}
      />
      {/* ─── Range tooltip — when cursor hovers the MIN/MAX line ─── */}
      {/* Suppressed when a trendline tooltip is active to prevent two */}
      {/* level-info tooltips from stacking on top of each other on the */}
      {/* same hit zone (single-tooltip rule). */}
      {rangeTip && !trendTip && (() => {
        const t = tLm(lang).range_tooltip || {};
        const isHi = rangeTip.kind === "hi";
        const prefix = isHi ? (t.max_prefix || "MAX seen") : (t.min_prefix || "MIN seen");
        const onWord = t.on || "on";
        const candleWord = t.candle_of || "candle";
        let dateTxt = "";
        if (rangeTip.meta?.t) {
          try {
            const locale = lang === "en" ? "en-US" : "es-ES";
            dateTxt = new Date(rangeTip.meta.t).toLocaleString(locale, {
              day: "numeric", month: "short",
              hour: "2-digit", minute: "2-digit", hour12: false,
            }).replace(",", "").replace(".", "");
          } catch { /* ignore */ }
        }
        // Position the tooltip ABOVE the cursor by default; flip below when
        // it would go off the top of the canvas. The on-canvas "MAX/MIN
        // RANGO" label box sits at ±8px around the line, so we add a 16px
        // safety gap (total = TIP_H + 16) to make sure the tooltip never
        // sits on top of its own origin label. Horizontally clamp inside
        // the wrapper width so the tooltip never overflows the viewport
        // on narrow mobile screens (375-414px).
        const wrapperW = rootRef.current?.getBoundingClientRect().width || 0;
        const TIP_W = Math.min(240, Math.max(160, wrapperW - 16));
        const TIP_H = 36;
        const LABEL_CLEAR = 16;  // gap = label half-height(8) + buffer(8)
        let px = rangeTip.x - TIP_W / 2;
        if (wrapperW > 0) {
          if (px + TIP_W > wrapperW - 8) px = wrapperW - TIP_W - 8;
          if (px < 8) px = 8;
        } else if (px < 8) {
          px = 8;
        }
        const py = rangeTip.y < TIP_H + LABEL_CLEAR + 8
          ? rangeTip.y + LABEL_CLEAR + 6        // flip below the line+label
          : rangeTip.y - TIP_H - LABEL_CLEAR;   // sit above with gap clear of label
        return (
          <div
            data-testid={`range-tooltip-${rangeTip.kind}`}
            className="pointer-events-none absolute z-10 rounded-md px-2.5 py-1.5"
            style={{
              left: px,
              top: py,
              background: "rgba(8,5,8,0.94)",
              border: "1px solid rgba(34,211,238,0.55)",
              color: "#22d3ee",
              fontFamily: "Inter, ui-sans-serif",
              fontSize: 10.5,
              fontWeight: 600,
              width: TIP_W,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <div>
              {prefix} {onWord} <span style={{ color: "#fff" }}>{dateTxt || "—"}</span>
            </div>
            <div style={{ color: "rgba(34,211,238,0.7)", fontSize: 9.5, marginTop: 1 }}>
              {candleWord} {timeframe}
            </div>
          </div>
        );
      })()}
      {/* ─── Trendline tooltip — pivot breakdown on hover/tap ─── */}
      {trendTip && trendHitRef.current && (() => {
        const tt = tLm(lang).trend_tooltip || {};
        const tHit = trendHitRef.current;
        const locale = lang === "en" ? "en-US" : "es-ES";
        const fmtDate = (ts) => {
          if (!ts) return "—";
          try {
            return new Date(ts).toLocaleString(locale, {
              day: "numeric", month: "short",
              hour: "2-digit", minute: "2-digit", hour12: false,
            }).replace(",", "").replace(".", "");
          } catch { return "—"; }
        };
        const kindWord = tHit.kind === "up"
          ? (tt.kind_low || "Mínimo (swing low)")
          : (tt.kind_high || "Máximo (swing high)");
        const TIP_W_DEFAULT = 280;
        // Honor narrow viewports: cap the tooltip width to the wrapper's
        // available horizontal space (minus a 12px gutter on each side).
        const wrapperW = rootRef.current?.getBoundingClientRect().width || 0;
        const availableW = Math.max(160, wrapperW - 24);
        const TIP_W = Math.min(TIP_W_DEFAULT, availableW);
        // Approximate height: header(28) + summary(24) + 2 hint lines(~22) + 22px per pivot.
        const TIP_H = 28 + 24 + 22 + 22 * Math.max(1, tHit.points.length);
        // Default to the RIGHT of the cursor; if that overflows, flip LEFT.
        // If neither side fits (very narrow viewport), shift-in from the
        // closest edge so the tooltip never sticks out.
        let px = trendTip.x + 18;
        if (wrapperW > 0 && px + TIP_W > wrapperW - 8) {
          // Try flipping to the left of the cursor first.
          const leftFlip = trendTip.x - TIP_W - 18;
          if (leftFlip >= 8) {
            px = leftFlip;
          } else {
            // Shift in: anchor to the right edge with an 8px margin.
            px = Math.max(8, wrapperW - TIP_W - 8);
          }
        }
        if (px < 8) px = 8;
        // If tooltip would overflow below the canvas, anchor it above the cursor.
        // Use a 16px gap (instead of 8) so the tooltip never sits on top of
        // the "Tendencia ..." on-canvas badge or pivot dot that triggered it.
        const canvasH = (rootRef.current?.getBoundingClientRect().height || 600) - BOTTOM_AXIS;
        const py = trendTip.y + TIP_H + 24 > canvasH
          ? Math.max(8, trendTip.y - TIP_H - 16)
          : trendTip.y + 18;
        return (
          <div
            data-testid="trend-tooltip"
            className="pointer-events-none absolute z-10 rounded-md px-3 py-2"
            style={{
              left: px,
              top: py,
              background: "rgba(8,5,8,0.96)",
              border: "1px solid rgba(168,85,247,0.55)",
              color: "#e9d5ff",
              fontFamily: "Inter, ui-sans-serif",
              fontSize: 10.5,
              fontWeight: 500,
              width: TIP_W,
              maxWidth: TIP_W,
              whiteSpace: "normal",
              boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
            }}
          >
            <div
              style={{
                color: "#a855f7", fontWeight: 700, fontSize: 11,
                marginBottom: 4, display: "flex", justifyContent: "space-between",
                gap: 8, alignItems: "baseline",
              }}
            >
              <span>{tt.title || "Pivotes de la tendencia"}</span>
              <span style={{ color: "rgba(168,85,247,0.7)", fontSize: 9, fontWeight: 600 }}>
                {(tt.total_pivots || "{n} pivotes").replace("{n}", String(tHit.points.length))}
              </span>
            </div>
            {/* Δ% + duration summary — quick-read trend strength. */}
            {tHit.points.length >= 2 && (() => {
              const firstPt = tHit.points[0];
              const lastPt = tHit.points[tHit.points.length - 1];
              const pctRaw = firstPt.p > 0 ? ((lastPt.p - firstPt.p) / firstPt.p) * 100 : 0;
              const sign = pctRaw >= 0 ? "+" : "−";
              const pctTxt = `${sign}${Math.abs(pctRaw).toFixed(2)}%`;
              const durationCandles = Math.max(0, lastPt.i - firstPt.i);
              const txt = (tt.delta_summary || "Δ {pct} · {n} velas")
                .replace("{pct}", pctTxt)
                .replace("{n}", String(durationCandles));
              const trendUp = tHit.kind === "up";
              return (
                <div
                  data-testid="trend-tooltip-summary"
                  style={{
                    display: "inline-block",
                    padding: "2px 8px",
                    marginBottom: 6,
                    borderRadius: 4,
                    background: trendUp ? "rgba(0,208,132,0.10)" : "rgba(255,59,92,0.10)",
                    border: `1px solid ${trendUp ? "rgba(0,208,132,0.45)" : "rgba(255,59,92,0.45)"}`,
                    color: trendUp ? "#00d084" : "#ff3b5c",
                    fontFamily: "ui-monospace, monospace",
                    fontSize: 9.5,
                    fontWeight: 700,
                    letterSpacing: "0.01em",
                  }}
                >
                  {txt}
                </div>
              );
            })()}
            {tHit.points.length === 0 && (
              <div style={{ color: "rgba(255,255,255,0.5)", fontStyle: "italic" }}>
                {tt.empty || "Sin pivotes detectados."}
              </div>
            )}
            {tHit.points.map((pt, idx) => {
              const isAnchor = pt.role === "anchor";
              return (
                <div
                  key={`${pt.i}-${idx}`}
                  data-testid={`trend-tooltip-pivot-${idx}`}
                  style={{
                    display: "flex", gap: 6,
                    alignItems: "center",
                    padding: "2px 0",
                    borderTop: idx === 0 ? "none" : "1px dashed rgba(168,85,247,0.18)",
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      display: "inline-block", width: 8, height: 8, borderRadius: "50%",
                      background: isAnchor ? "#a855f7" : "rgba(168,85,247,0.45)",
                      border: isAnchor ? "1.5px solid #fff" : "1px solid rgba(168,85,247,0.6)",
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      color: isAnchor ? "#fff" : "rgba(233,213,255,0.9)",
                      fontWeight: isAnchor ? 700 : 500,
                      fontSize: 9.5,
                      minWidth: 78,
                      textTransform: "uppercase",
                      letterSpacing: "0.02em",
                    }}
                  >
                    {isAnchor ? (tt.anchor || "Anclaje") : (tt.intermediate || "Intermedio")}
                  </span>
                  <span style={{ color: "#fff", fontFamily: "ui-monospace, monospace", fontSize: 10.5 }}>
                    {fmtPrice(pt.p)}
                  </span>
                  <span style={{ color: "rgba(233,213,255,0.65)", marginLeft: "auto", fontSize: 9.5 }}>
                    {fmtDate(pt.t)}
                  </span>
                </div>
              );
            })}
            <div style={{ color: "rgba(255,255,255,0.45)", fontSize: 9, marginTop: 6, lineHeight: 1.35 }}>
              <span style={{ display: "block" }}>
                <span style={{ color: "#a855f7", fontWeight: 700 }}>●</span> {tt.hint_anchor}
              </span>
              <span style={{ display: "block" }}>
                <span style={{ color: "rgba(168,85,247,0.55)", fontWeight: 700 }}>○</span> {tt.hint_intermediate}
              </span>
              <span style={{ display: "block", marginTop: 2, opacity: 0.8 }}>
                {kindWord} · {timeframe}
              </span>
            </div>
          </div>
        );
      })()}
      {/* ─── Patterns tooltip (Double Top / Double Bottom) ─── */}
      {patternTip && patternsHitRef.current && (() => {
        const tt = tLm(lang).patterns || {};
        const pHit = patternsHitRef.current;
        const locale = lang === "en" ? "en-US" : "es-ES";
        const fmtDate = (ts) => {
          if (!ts) return "—";
          try {
            return new Date(ts).toLocaleString(locale, {
              day: "numeric", month: "short",
              hour: "2-digit", minute: "2-digit", hour12: false,
            }).replace(",", "").replace(".", "");
          } catch { return "—"; }
        };
        const wrapperW = rootRef.current?.getBoundingClientRect().width || 0;
        const availableW = Math.max(160, wrapperW - 24);
        const TIP_W = Math.min(290, availableW);
        const TIP_H = 28 + 22 * 5 + 22; // header + 5 rows + footer
        let px = patternTip.x + 18;
        if (wrapperW > 0 && px + TIP_W > wrapperW - 8) {
          const leftFlip = patternTip.x - TIP_W - 18;
          if (leftFlip >= 8) px = leftFlip;
          else px = Math.max(8, wrapperW - TIP_W - 8);
        }
        if (px < 8) px = 8;
        const canvasH = (rootRef.current?.getBoundingClientRect().height || 600) - BOTTOM_AXIS;
        const py = patternTip.y + TIP_H + 24 > canvasH
          ? Math.max(8, patternTip.y - TIP_H - 16)
          : patternTip.y + 18;
        const statusWord = pHit.confirmed
          ? (tt.status_confirmed || "confirmado")
          : (tt.status_forming || "formándose");
        let title;
        if (pHit.isTriangle) {
          const kindWord = pHit.kind === "ascending"
            ? (tt.triangle_ascending  || "Triángulo Ascendente")
            : pHit.kind === "descending"
              ? (tt.triangle_descending || "Triángulo Descendente")
              : (tt.triangle_symmetric  || "Triángulo Simétrico");
          title = `${kindWord} · ${statusWord}`;
        } else if (pHit.isHCH) {
          const kindWord = pHit.isInverse
            ? (tt.label_inv_hs || "HCH Invertido")
            : (tt.label_hs || "Hombro-Cabeza-Hombro");
          title = `${kindWord} · ${statusWord}`;
        } else {
          const titleTpl = pHit.isTop
            ? (tt.tooltip_title_top    || "Doble Techo · {status}")
            : (tt.tooltip_title_bottom || "Doble Suelo · {status}");
          title = titleTpl.replace("{status}", statusWord);
        }
        const labelFor = (k) => {
          if (k === "peak1")   return tt.tooltip_peak1   || "Pico 1";
          if (k === "peak2")   return tt.tooltip_peak2   || "Pico 2";
          if (k === "valley")  return tt.tooltip_valley  || "Valle (neckline)";
          if (k === "bottom1") return tt.tooltip_bottom1 || "Mínimo 1";
          if (k === "bottom2") return tt.tooltip_bottom2 || "Mínimo 2";
          if (k === "ridge")   return tt.tooltip_ridge   || "Cresta (neckline)";
          if (k === "high")    return tt.tooltip_high_pivot || "Pivote alto";
          if (k === "low")     return tt.tooltip_low_pivot  || "Pivote bajo";
          if (k === "ls")      return tt.tooltip_ls   || "Hombro Izq.";
          if (k === "head")    return tt.tooltip_head || "Cabeza";
          if (k === "rs")      return tt.tooltip_rs   || "Hombro Der.";
          return k;
        };
        const tolTxt = (() => {
          const th = pHit.thresholds || {};
          if (pHit.isTriangle) {
            const flatPct = (th.flat || 0).toFixed(4);
            return th.calibrated
              ? (tt.tooltip_triangle_flat || "Flat {coin}: ≤{flat}%/vela").replace("{coin}", th.coin || "").replace("{flat}", flatPct)
              : (tt.tooltip_triangle_flat_pooled || "Flat pooled: ≤{flat}%/vela ({coin} sin calibrar)").replace("{coin}", th.coin || "").replace("{flat}", flatPct);
          }
          if (pHit.isHCH) {
            const headStr = (th.head_min || 0).toFixed(2);
            const shStr = (th.shoulder_max || 0).toFixed(2);
            return th.calibrated
              ? (tt.tooltip_hch_calibrated || "Cabeza ≥ {head}% · Δ hombros ≤ {shoulder}% ({coin})")
                  .replace("{head}", headStr).replace("{shoulder}", shStr).replace("{coin}", th.coin || "")
              : (tt.tooltip_hch_pooled || "Cabeza ≥ {head}% · Δ hombros ≤ {shoulder}% (pooled, {coin} sin calibrar)")
                  .replace("{head}", headStr).replace("{shoulder}", shStr).replace("{coin}", th.coin || "");
          }
          const tolPct = (th.peakTol || 0).toFixed(2);
          return th.calibrated
            ? (tt.tooltip_calibrated || "Tolerancia {coin}: ±{tol}%").replace("{coin}", th.coin || "").replace("{tol}", tolPct)
            : (tt.tooltip_fallback || "Tolerancia pooled: ±{tol}% ({coin} sin calibrar)").replace("{coin}", th.coin || "").replace("{tol}", tolPct);
        })();
        return (
          <div
            data-testid="pattern-tooltip"
            className="pointer-events-none absolute z-10 rounded-md px-3 py-2"
            style={{
              left: px, top: py,
              background: "rgba(8,5,8,0.96)",
              border: `1px solid ${pHit.confirmed ? "#ec4899" : "rgba(236,72,153,0.55)"}`,
              color: "#fbcfe8",
              fontFamily: "Inter, ui-sans-serif",
              fontSize: 10.5, fontWeight: 500,
              width: TIP_W, maxWidth: TIP_W,
              boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
              whiteSpace: "normal",
            }}
          >
            <div
              data-testid="pattern-tooltip-title"
              style={{
                color: "#ec4899", fontWeight: 700, fontSize: 11,
                marginBottom: 4,
                display: "flex", alignItems: "baseline", gap: 8, justifyContent: "space-between",
              }}
            >
              <span>{title}</span>
              {!pHit.confirmed && (
                <span style={{ color: "rgba(236,72,153,0.65)", fontSize: 9, fontWeight: 600 }}>
                  {pHit.isTriangle
                    ? (tt.pending_break_triangle || "vértice pendiente")
                    : (tt.pending_break || "falta romper neckline")}
                </span>
              )}
            </div>
            {pHit.points.map((pt, idx) => (
              <div
                key={`${pt.i}-${idx}`}
                data-testid={`pattern-tooltip-row-${idx}`}
                style={{
                  display: "flex", gap: 6, alignItems: "center", padding: "2px 0",
                  borderTop: idx === 0 ? "none" : "1px dashed rgba(236,72,153,0.18)",
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 8, height: 8, borderRadius: "50%",
                    background: "#ec4899",
                    border: "1.5px solid #fff",
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    color: "#fff", fontWeight: 700, fontSize: 9.5,
                    minWidth: 86, textTransform: "uppercase", letterSpacing: "0.02em",
                  }}
                >
                  {labelFor(pt.kind)}
                </span>
                <span style={{ color: "#fff", fontFamily: "ui-monospace, monospace", fontSize: 10.5 }}>
                  {fmtPrice(pt.p)}
                </span>
                <span style={{ color: "rgba(251,207,232,0.65)", marginLeft: "auto", fontSize: 9.5 }}>
                  {fmtDate(pt.t)}
                </span>
              </div>
            ))}
            {/* Neckline row — only for Double Top/Bottom (Triangle has 2 lines,
                H&S has a diagonal neckline shown in the badge metrics). */}
            {!pHit.isTriangle && !pHit.isHCH && (
              <div
                data-testid="pattern-tooltip-neckline"
                style={{
                  display: "flex", gap: 6, alignItems: "center", padding: "2px 0",
                  borderTop: "1px dashed rgba(236,72,153,0.18)",
                }}
              >
                <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 2, background: "rgba(236,72,153,0.4)", border: "1px solid #ec4899", flexShrink: 0 }} />
                <span style={{ color: "rgba(251,207,232,0.85)", fontWeight: 700, fontSize: 9.5, minWidth: 86, textTransform: "uppercase", letterSpacing: "0.02em" }}>
                  {tt.tooltip_neckline || "Neckline"}
                </span>
                <span style={{ color: "#fff", fontFamily: "ui-monospace, monospace", fontSize: 10.5 }}>
                  {fmtPrice(pHit.neckline)}
                </span>
              </div>
            )}
            {/* HCH metrics row — head/shoulder diffs (replaces the horizontal
                neckline row that doesn't apply to a diagonal neckline). */}
            {pHit.isHCH && pHit.metrics && (
              <div
                data-testid="pattern-tooltip-hch-metrics"
                style={{
                  display: "flex", gap: 6, alignItems: "center", padding: "2px 0",
                  borderTop: "1px dashed rgba(236,72,153,0.18)",
                  flexWrap: "wrap",
                }}
              >
                <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 2, background: "rgba(236,72,153,0.4)", border: "1px solid #ec4899", flexShrink: 0 }} />
                <span style={{ color: "#fff", fontFamily: "ui-monospace, monospace", fontSize: 10 }}>
                  {(tt.tooltip_head_diff || "Δ cabeza-hombros")}: {pHit.metrics.head_diff_pct.toFixed(2)}%
                </span>
                <span style={{ color: "rgba(251,207,232,0.65)", marginLeft: "auto", fontSize: 9.5, fontFamily: "ui-monospace, monospace" }}>
                  {(tt.tooltip_shoulder_diff || "Δ hombros")}: {pHit.metrics.shoulder_diff_pct.toFixed(2)}%
                </span>
              </div>
            )}
            {/* Break row (only if confirmed). */}
            {pHit.confirmed && pHit.breakPrice != null && (
              <div
                data-testid="pattern-tooltip-break"
                style={{
                  display: "flex", gap: 6, alignItems: "center", padding: "2px 0",
                  borderTop: "1px dashed rgba(236,72,153,0.18)",
                }}
              >
                <span aria-hidden="true" style={{ width: 8, height: 8, color: "#ec4899", flexShrink: 0, textAlign: "center", lineHeight: "8px", fontSize: 12, fontWeight: 900 }}>✕</span>
                <span style={{ color: "#fff", fontWeight: 700, fontSize: 9.5, minWidth: 86, textTransform: "uppercase", letterSpacing: "0.02em" }}>
                  {tt.tooltip_break || "Rotura"}
                </span>
                <span style={{ color: "#fff", fontFamily: "ui-monospace, monospace", fontSize: 10.5 }}>
                  {fmtPrice(pHit.breakPrice)}
                </span>
              </div>
            )}
            <div style={{ color: "rgba(251,207,232,0.55)", fontSize: 9, marginTop: 6, lineHeight: 1.35 }}>
              {tolTxt} · {timeframe}
            </div>
          </div>
        );
      })()}
      {/* ─── Candle pattern tooltip ─── */}
      {candleTip && candleTip.hit && (() => {
        const tt = tLm(lang).patterns || {};
        const hit = candleTip.hit;
        const sigColor = hit.signal === "bullish" ? "#22c55e" : hit.signal === "bearish" ? "#ef4444" : "#9ca3af";
        const sigBorder = hit.signal === "bullish" ? "rgba(34,197,94,0.55)" : hit.signal === "bearish" ? "rgba(239,68,68,0.55)" : "rgba(148,163,184,0.55)";
        const wrapperW = rootRef.current?.getBoundingClientRect().width || 0;
        const TIP_W = Math.min(240, Math.max(160, wrapperW - 24));
        const TIP_H = 110;
        let px = candleTip.x + 18;
        if (wrapperW > 0 && px + TIP_W > wrapperW - 8) {
          const leftFlip = candleTip.x - TIP_W - 18;
          if (leftFlip >= 8) px = leftFlip;
          else px = Math.max(8, wrapperW - TIP_W - 8);
        }
        if (px < 8) px = 8;
        const canvasH = (rootRef.current?.getBoundingClientRect().height || 600) - BOTTOM_AXIS;
        const py = candleTip.y + TIP_H + 24 > canvasH
          ? Math.max(8, candleTip.y - TIP_H - 16)
          : candleTip.y + 18;
        const patternLabel = ({
          hammer: tt.candle_hammer || "Hammer",
          hanging_man: tt.candle_hanging_man || "Hanging Man",
          inverted_hammer: tt.candle_inverted_hammer || "Inverted Hammer",
          shooting_star: tt.candle_shooting_star || "Shooting Star",
          doji: tt.candle_doji || "Doji",
          engulfing_bull: tt.candle_engulfing_bull || "Engulfing Alcista",
          engulfing_bear: tt.candle_engulfing_bear || "Engulfing Bajista",
        })[hit.pattern] || hit.pattern;
        const sigLabel = hit.signal === "bullish"
          ? (tt.candle_signal_bullish || "alcista")
          : hit.signal === "bearish"
            ? (tt.candle_signal_bearish || "bajista")
            : (tt.candle_signal_neutral || "neutral");
        const m = hit.metrics || {};
        return (
          <div
            data-testid="candle-tooltip"
            className="pointer-events-none absolute z-10 rounded-md px-3 py-2"
            style={{
              left: px, top: py,
              background: "rgba(8,5,8,0.96)",
              border: `1px solid ${sigBorder}`,
              color: "#e2e8f0",
              fontFamily: "Inter, ui-sans-serif",
              fontSize: 10.5, fontWeight: 500,
              width: TIP_W, maxWidth: TIP_W,
              boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
              whiteSpace: "normal",
            }}
          >
            <div
              data-testid="candle-tooltip-title"
              style={{
                color: sigColor, fontWeight: 700, fontSize: 11,
                marginBottom: 4,
                display: "flex", alignItems: "baseline", gap: 8, justifyContent: "space-between",
              }}
            >
              <span>🕯 {patternLabel}</span>
              <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase" }}>{sigLabel}</span>
            </div>
            {m.body_ratio != null && (
              <div style={{ display: "flex", gap: 6, padding: "2px 0", borderTop: "1px dashed rgba(148,163,184,0.18)" }}>
                <span style={{ color: "rgba(226,232,240,0.7)", minWidth: 96, fontSize: 9.5, textTransform: "uppercase", letterSpacing: "0.02em" }}>
                  {tt.candle_body_ratio || "Cuerpo / rango"}
                </span>
                <span style={{ color: "#fff", fontFamily: "ui-monospace, monospace" }}>
                  {(m.body_ratio * 100).toFixed(2)}%
                </span>
              </div>
            )}
            {m.engulf_ratio != null && (
              <div style={{ display: "flex", gap: 6, padding: "2px 0", borderTop: "1px dashed rgba(148,163,184,0.18)" }}>
                <span style={{ color: "rgba(226,232,240,0.7)", minWidth: 96, fontSize: 9.5, textTransform: "uppercase", letterSpacing: "0.02em" }}>
                  {tt.candle_engulf_ratio || "Razón engulf"}
                </span>
                <span style={{ color: "#fff", fontFamily: "ui-monospace, monospace" }}>
                  ×{m.engulf_ratio.toFixed(2)}
                </span>
              </div>
            )}
            {m.lower_wick != null && (
              <div style={{ display: "flex", gap: 6, padding: "2px 0", borderTop: "1px dashed rgba(148,163,184,0.18)" }}>
                <span style={{ color: "rgba(226,232,240,0.7)", minWidth: 96, fontSize: 9.5, textTransform: "uppercase", letterSpacing: "0.02em" }}>
                  {tt.candle_lower_wick || "Mecha inferior"}
                </span>
                <span style={{ color: "#fff", fontFamily: "ui-monospace, monospace" }}>
                  {(m.lower_wick * 100).toFixed(1)}%
                </span>
              </div>
            )}
            {m.upper_wick != null && (m.lower_wick == null || hit.pattern === "inverted_hammer" || hit.pattern === "shooting_star" || hit.pattern === "doji") && (
              <div style={{ display: "flex", gap: 6, padding: "2px 0", borderTop: "1px dashed rgba(148,163,184,0.18)" }}>
                <span style={{ color: "rgba(226,232,240,0.7)", minWidth: 96, fontSize: 9.5, textTransform: "uppercase", letterSpacing: "0.02em" }}>
                  {tt.candle_upper_wick || "Mecha superior"}
                </span>
                <span style={{ color: "#fff", fontFamily: "ui-monospace, monospace" }}>
                  {(m.upper_wick * 100).toFixed(1)}%
                </span>
              </div>
            )}
            {m.drift_pct != null && (
              <div style={{ display: "flex", gap: 6, padding: "2px 0", borderTop: "1px dashed rgba(148,163,184,0.18)" }}>
                <span style={{ color: "rgba(226,232,240,0.7)", minWidth: 96, fontSize: 9.5, textTransform: "uppercase", letterSpacing: "0.02em" }}>
                  {tt.candle_drift || "Drift (ctx)"}
                </span>
                <span style={{ color: "#fff", fontFamily: "ui-monospace, monospace" }}>
                  {m.drift_pct >= 0 ? "+" : ""}{m.drift_pct.toFixed(2)}%
                </span>
              </div>
            )}
            <div style={{ color: "rgba(226,232,240,0.55)", fontSize: 9, marginTop: 6, lineHeight: 1.35 }}>
              i={hit.i} · {timeframe}
              {hit.thresholds?.coin ? ` · ${hit.thresholds.coin}${hit.thresholds.calibrated === false ? " (pooled)" : ""}` : ""}
            </div>
          </div>
        );
      })()}
      {/* ─── Hover tooltip — detailed bucket info under cursor ─── */}
      {/* Single-tooltip rule: suppressed if rangeTip, trendTip, patternTip or candleTip is active. */}
      {hoverBucket && !trendTip && !rangeTip && !patternTip && !candleTip && (() => {
        const { mouseX, mouseY, bucket, canvasW, canvasH } = hoverBucket;
        const chartW = canvasW - RIGHT_AXIS;
        const chartH = canvasH - BOTTOM_AXIS;
        const intensityRaw = Number(bucket.intensity) || 0;
        const intensityPct = Math.round(Math.max(0, Math.min(1, intensityRaw)) * 100);
        // Number of contributing snapshots (proxy): exchanges_active scaled by intensity.
        const numExchanges = Math.max(1, Number(stats?.exchanges_active) || 1);
        const numEventos = Math.max(1, Math.round((intensityRaw || 0.05) * numExchanges * 3));
        // Total USD in this bucket's hourly slice (proxy): share of OI proportional to intensity.
        const sumIntensity = (clusters || []).reduce((acc, c) => acc + (Number(c.intensity) || 0), 0) || 1;
        const oiTotal = Number(stats?.open_interest_total_usd) || 0;
        const totalUsd = (intensityRaw / sumIntensity) * oiTotal;
        const totalHora = oiTotal * 0.04; // proxy for "hourly" rolling volume
        const fmtUsd = (n) => {
          if (!Number.isFinite(n) || n <= 0) return "—";
          if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
          if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
          if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
          return `$${n.toFixed(0)}`;
        };
        // Timestamp = last candle time when available, else snapshot stats ts.
        const lastT = candles && candles.length ? candles[candles.length - 1].t : null;
        const tsLabel = lastT
          ? new Date(lastT).toISOString().replace("T", " ").slice(0, 16) + " UTC"
          : "—";
        // Position tooltip — keep it inside the wrapper area, +15px from cursor.
        // On narrow viewports the bucket box may not fit at either side of
        // the cursor; clamp width and shift-in from the closest edge so it
        // never sticks out of the viewport.
        const wrapperW = rootRef.current?.getBoundingClientRect().width || canvasW;
        const TOOLTIP_W = Math.min(200, Math.max(140, wrapperW - 24));
        const TOOLTIP_H = 96;
        let left = mouseX + 15;
        let top = mouseY - 10;
        if (left + TOOLTIP_W > wrapperW - 8) left = mouseX - TOOLTIP_W - 12;
        if (left < 8) left = Math.max(8, wrapperW - TOOLTIP_W - 8);
        if (left < 8) left = 8;
        if (top + TOOLTIP_H > chartH - 4) top = chartH - TOOLTIP_H - 4;
        if (top < 4) top = 4;
        const intensityCol = intensityPct >= 70 ? "#ff3b5c" : intensityPct >= 40 ? "#ff8c42" : "#86efac";
        return (
          <>
            <div
              data-testid="liqmap-hover-line"
              className="pointer-events-none absolute"
              style={{
                left: 0, top: mouseY - 0.5,
                width: chartW, height: 1,
                background: "repeating-linear-gradient(to right, rgba(249,115,22,0.35) 0 4px, transparent 4px 8px)",
                zIndex: 49,
              }}
            />
            <div
              data-testid="liqmap-hover-tooltip"
              className="pointer-events-none absolute font-mono"
              style={{
                left, top,
                width: TOOLTIP_W,
                background: "rgba(8,5,8,0.97)",
                border: "1px solid rgba(249,115,22,0.4)",
                borderRadius: 8,
                padding: "8px 12px",
                zIndex: 50,
                backdropFilter: "blur(8px)",
              }}
            >
              <div className="text-[9px] tracking-wider" style={{ color: "#666" }}>NIVEL</div>
              <div className="text-[14px] font-bold" style={{ color: "#f97316" }}>
                {fmtPrice(bucket.price)}
              </div>
              <div className="mt-1 flex items-center justify-between">
                <span className="text-[9px] tracking-wider" style={{ color: "#666" }}>LIQUIDEZ</span>
                <span className="text-[11px] font-bold" style={{ color: intensityCol }}>
                  {intensityPct}%
                </span>
              </div>
              <div className="mt-1 text-[10px]" style={{ color: "#aaa" }}>
                {fmtUsd(totalUsd)} · #{numEventos} eventos
              </div>
              <div className="text-[10px]" style={{ color: "#888" }}>
                Total hora: {fmtUsd(totalHora)}
              </div>
              <div className="mt-1 text-[8px]" style={{ color: "#555" }}>
                {tsLabel}
              </div>
            </div>
          </>
        );
      })()}
      {/* ─── Candle mini-legend — left edge, vertically centered ─── */}
      {/* Visible only when the Candles layer is on AND ≥1 pattern was   */}
      {/* detected. Collapsible (state persisted to localStorage).        */}
      {/* Shows the count + 4 shape rows (✕ doji, ▼ hammer family,        */}
      {/* ▲ inverted-hammer family, ◆ engulfing) + ●N cluster marker.     */}
      {showCandles && candlePatterns.length > 0 && (() => {
        const t = tLm(lang);
        const count = candlePatterns.length;
        const countMsg = (t.candle_legend_count || "{n} pattern(s) in range").replace("{n}", count);
        const title = t.candle_legend_title || "Candles";
        const toggleTitle = candleLegendCollapsed
          ? (t.candle_legend_expand || "Expand legend")
          : (t.candle_legend_collapse || "Collapse legend");
        // Compact (collapsed) variant: only a 🕯 icon + count + chevron.
        if (candleLegendCollapsed) {
          return (
            <button
              type="button"
              onClick={() => setCandleLegendCollapsed(false)}
              data-testid="candle-legend-toggle"
              data-collapsed="1"
              title={toggleTitle}
              className="absolute z-10 flex items-center gap-1.5 px-2 py-1.5 transition-colors"
              style={{
                left: 6,
                top: "50%",
                transform: "translateY(-50%)",
                background: "rgba(8,5,8,0.88)",
                border: "1px solid rgba(148,163,184,0.35)",
                borderRadius: 6,
                color: "#e2e8f0",
                fontFamily: "Inter, ui-sans-serif",
                fontSize: 10.5,
                fontWeight: 600,
                backdropFilter: "blur(6px)",
                pointerEvents: "auto",
                cursor: "pointer",
              }}
            >
              <span aria-hidden="true">🕯</span>
              <span data-testid="candle-legend-count">{count}</span>
              <span style={{ color: "rgba(148,163,184,0.7)", fontSize: 9 }}>▶</span>
            </button>
          );
        }
        // Expanded variant: header + 5 rows. Each row uses inline SVG so
        // shapes match the canvas markers (✕ / ▼ / ▲ / ◆ / ●N).
        const renderRow = (key, svg, label) => (
          <div
            key={key}
            data-testid={`candle-legend-row-${key}`}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "3px 0",
              fontSize: 10, lineHeight: 1.25,
            }}
          >
            <span style={{ width: 16, height: 16, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {svg}
            </span>
            <span style={{ color: "rgba(226,232,240,0.9)" }}>{label}</span>
          </div>
        );
        return (
          <div
            data-testid="candle-legend"
            data-collapsed="0"
            className="absolute z-10"
            style={{
              left: 6,
              top: "50%",
              transform: "translateY(-50%)",
              background: "rgba(8,5,8,0.92)",
              border: "1px solid rgba(148,163,184,0.35)",
              borderRadius: 8,
              padding: "8px 10px 8px 10px",
              color: "#e2e8f0",
              fontFamily: "Inter, ui-sans-serif",
              backdropFilter: "blur(8px)",
              minWidth: 168,
              maxWidth: 220,
              pointerEvents: "auto",
              boxShadow: "0 6px 20px rgba(0,0,0,0.35)",
            }}
          >
            <div
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 8, marginBottom: 6,
                borderBottom: "1px dashed rgba(148,163,184,0.25)", paddingBottom: 5,
              }}
            >
              <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <span aria-hidden="true" style={{ fontSize: 12 }}>🕯</span>
                <span style={{ fontWeight: 700, fontSize: 11, letterSpacing: "0.02em" }}>{title}</span>
                <span
                  data-testid="candle-legend-count"
                  style={{
                    fontSize: 10, fontWeight: 700,
                    color: "#22d3ee",
                    fontFamily: "ui-monospace, monospace",
                  }}
                >
                  {count}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setCandleLegendCollapsed(true)}
                data-testid="candle-legend-toggle"
                title={toggleTitle}
                className="transition-colors"
                style={{
                  background: "transparent",
                  border: "none",
                  color: "rgba(148,163,184,0.8)",
                  cursor: "pointer",
                  fontSize: 11,
                  lineHeight: 1,
                  padding: "0 2px",
                }}
              >
                ◀
              </button>
            </div>
            <div data-testid="candle-legend-rows">
              {renderRow(
                "doji",
                <svg width="14" height="14" viewBox="-7 -7 14 14" aria-hidden="true">
                  <line x1="-4" y1="-4" x2="4" y2="4" stroke="#9ca3af" strokeWidth="2.2" strokeLinecap="round" />
                  <line x1="4" y1="-4" x2="-4" y2="4" stroke="#9ca3af" strokeWidth="2.2" strokeLinecap="round" />
                </svg>,
                t.candle_legend_doji || "Doji",
              )}
              {renderRow(
                "hammer",
                <svg width="14" height="14" viewBox="-7 -7 14 14" aria-hidden="true">
                  <polygon points="-5,-4 5,-4 0,5" fill="#22c55e" stroke="rgba(255,255,255,0.85)" strokeWidth="1" />
                </svg>,
                t.candle_legend_hammer || "Hammer · Hanging Man",
              )}
              {renderRow(
                "inv-hammer",
                <svg width="14" height="14" viewBox="-7 -7 14 14" aria-hidden="true">
                  <polygon points="-5,4 5,4 0,-5" fill="#ef4444" stroke="rgba(255,255,255,0.85)" strokeWidth="1" />
                </svg>,
                t.candle_legend_inv_hammer || "Inv. Hammer · Shooting Star",
              )}
              {renderRow(
                "engulfing",
                <svg width="14" height="14" viewBox="-7 -7 14 14" aria-hidden="true">
                  <polygon points="0,-5 5,0 0,5 -5,0" fill="#22c55e" stroke="rgba(255,255,255,0.85)" strokeWidth="1" />
                </svg>,
                t.candle_legend_engulfing || "Engulfing",
              )}
              {renderRow(
                "cluster",
                <svg width="14" height="14" viewBox="-7 -7 14 14" aria-hidden="true">
                  <circle cx="0" cy="0" r="6" fill="#9ca3af" stroke="rgba(255,255,255,0.85)" strokeWidth="1" />
                  <text x="0" y="0.5" textAnchor="middle" dominantBaseline="middle" fontSize="7" fontWeight="700" fill="#0b0b10" fontFamily="ui-sans-serif">N</text>
                </svg>,
                t.candle_legend_cluster || "Cluster (N nearby candles)",
              )}
            </div>
            <div
              style={{
                marginTop: 6, paddingTop: 5,
                borderTop: "1px dashed rgba(148,163,184,0.22)",
                color: "rgba(148,163,184,0.75)",
                fontSize: 9, lineHeight: 1.3,
              }}
            >
              {countMsg}
            </div>
          </div>
        );
      })()}
      {/* ─── B3b — Right-click contextual menu ─── */}
      {/* DOM overlay positioned with `position: fixed` at the click's   */}
      {/* clientX/clientY. z-40 sits above the drawing toolbar (z-15)    */}
      {/* and the filter toolbar (z-30). Auto-closes on outside-click    */}
      {/* (capture-phase listener in the useEffect above) or Esc.        */}
      {contextMenu && ctxMenuTarget && (() => {
        const tD = tLm(lang).patterns?.drawing || {};
        const lockLabel = ctxMenuTarget.locked
          ? (tD.ctx_unlock || "Desbloquear")
          : (tD.ctx_lock   || "Bloquear");
        const hideLabel = ctxMenuTarget.hidden
          ? (tD.ctx_show   || "Mostrar")
          : (tD.ctx_hide   || "Ocultar");
        const cloneLabel = tD.ctx_clone || "Clonar";
        const deleteLabel = tD.ctx_delete || "Eliminar";
        const colorLabel = tD.ctx_color || "Color";
        const widthLabel = tD.ctx_width || "Grosor";
        const widthLockedTip = tD.ctx_width_locked_tooltip
          || "Fijo para mantener el efecto resaltador";
        const widthLabels = {
          thin:   tD.ctx_width_thin   || "Fino",
          normal: tD.ctx_width_normal || "Normal",
          thick:  tD.ctx_width_thick  || "Grueso",
          bold:   tD.ctx_width_bold   || "Bold",
        };
        const currentColor = ctxMenuTarget.color || drawingEngine.DEFAULT_STYLE.color;
        const currentWidth = ctxMenuTarget.width || drawingEngine.DEFAULT_STYLE.width;
        const widthLocked = drawingEngine.isStyleWidthLocked(ctxMenuTarget);
        const items = [
          { id: "lock",  label: lockLabel,  onClick: ctxToggleLock,  testid: "liqmap-ctx-lock"   },
          { id: "hide",  label: hideLabel,  onClick: ctxToggleHide,  testid: "liqmap-ctx-hide"   },
          { id: "clone", label: cloneLabel, onClick: ctxClone,       testid: "liqmap-ctx-clone"  },
          { id: "del",   label: deleteLabel, onClick: ctxDelete,
            testid: "liqmap-ctx-delete",
            disabled: !!ctxMenuTarget.locked },
        ];
        // Clamp menu so it doesn't overflow the viewport. With the B7b
        // style pickers the menu is ~250px tall (header buttons + color
        // strip + width strip + footer buttons), wider too (~220px).
        const W = 226, H = 240;
        const vx = Math.min(window.innerWidth - W - 8, contextMenu.x);
        const vy = Math.min(window.innerHeight - H - 8, contextMenu.y);
        return (
          <div
            id={`liqmap-ctx-menu-${contextMenu.drawingId}`}
            data-testid="liqmap-drawing-ctx-menu"
            data-drawing-id={contextMenu.drawingId}
            style={{
              position: "fixed",
              top: vy, left: vx,
              minWidth: W,
              zIndex: 40,
              background: "rgba(8,5,8,0.95)",
              border: "1px solid rgba(34,211,238,0.4)",
              borderRadius: 8,
              padding: 4,
              fontFamily: "Inter, ui-sans-serif",
              boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
              backdropFilter: "blur(8px)",
            }}
          >
            {/* Lock + Hide + Clone */}
            {items.slice(0, 3).map((it) => (
              <button
                key={it.id}
                type="button"
                onClick={it.onClick}
                disabled={it.disabled}
                data-testid={it.testid}
                style={{
                  display: "block",
                  width: "100%",
                  padding: "7px 10px",
                  textAlign: "left",
                  background: "transparent",
                  border: "none",
                  color: it.disabled ? "rgba(148,163,184,0.5)" : "#e2e8f0",
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: it.disabled ? "not-allowed" : "pointer",
                  borderRadius: 5,
                  transition: "background 120ms",
                }}
                onMouseEnter={(e) => { if (!it.disabled) e.currentTarget.style.background = "rgba(34,211,238,0.12)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                {it.label}
              </button>
            ))}

            {/* ── B7b — Color inline picker ── */}
            <div
              data-testid="liqmap-ctx-color-section"
              style={{
                padding: "6px 10px 4px 10px",
                borderTop: "1px solid rgba(255,255,255,0.06)",
                marginTop: 2,
              }}
            >
              <div style={{
                fontSize: 10, fontWeight: 700,
                textTransform: "uppercase", letterSpacing: 0.5,
                color: "rgba(226,232,240,0.55)", marginBottom: 4,
              }}>{colorLabel}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {drawingEngine.STYLE_SWATCHES.map((sw) => {
                  const isActive = currentColor.toLowerCase() === sw.toLowerCase();
                  return (
                    <button
                      key={sw}
                      type="button"
                      onClick={() => ctxSetColor(sw)}
                      data-testid={`liqmap-ctx-color-swatch-${sw.replace("#","")}`}
                      title={sw}
                      style={{
                        width: 16, height: 16,
                        borderRadius: "50%",
                        background: sw,
                        border: isActive
                          ? "2px solid #fff"
                          : "1px solid rgba(255,255,255,0.2)",
                        cursor: "pointer",
                        padding: 0,
                      }}
                    />
                  );
                })}
                {/* Custom color chip — opens native <input type="color"> */}
                <label
                  data-testid="liqmap-ctx-color-custom-chip"
                  title={tD.ctx_color_custom || "Personalizado"}
                  style={{
                    width: 16, height: 16,
                    borderRadius: "50%",
                    background: "conic-gradient(#f87171,#fb923c,#facc15,#10b981,#22d3ee,#3b82f6,#a855f7,#ec4899,#f87171)",
                    border: "1px solid rgba(255,255,255,0.2)",
                    cursor: "pointer",
                    display: "inline-block",
                  }}
                >
                  <input
                    type="color"
                    value={currentColor}
                    onChange={(e) => ctxSetColor(e.target.value)}
                    data-testid="liqmap-ctx-color-custom-input"
                    style={{
                      position: "absolute",
                      opacity: 0, width: 0, height: 0,
                      pointerEvents: "none",
                    }}
                  />
                </label>
              </div>
            </div>

            {/* ── B7b — Grosor inline picker ── */}
            <div
              data-testid="liqmap-ctx-width-section"
              title={widthLocked ? widthLockedTip : undefined}
              style={{
                padding: "6px 10px 8px 10px",
                borderTop: "1px solid rgba(255,255,255,0.06)",
                opacity: widthLocked ? 0.45 : 1,
              }}
            >
              <div style={{
                fontSize: 10, fontWeight: 700,
                textTransform: "uppercase", letterSpacing: 0.5,
                color: "rgba(226,232,240,0.55)", marginBottom: 4,
              }}>{widthLabel}</div>
              <div style={{ display: "flex", gap: 4 }}>
                {drawingEngine.STYLE_WIDTHS.map((w) => {
                  const isActive = Math.abs(currentWidth - w.px) < 0.01;
                  return (
                    <button
                      key={w.id}
                      type="button"
                      onClick={() => ctxSetWidth(w.px)}
                      disabled={widthLocked}
                      data-testid={`liqmap-ctx-width-${w.id}`}
                      title={widthLabels[w.id]}
                      style={{
                        flex: 1,
                        padding: "4px 0",
                        background: isActive
                          ? "rgba(34,211,238,0.18)" : "rgba(255,255,255,0.04)",
                        border: isActive
                          ? "1px solid rgba(34,211,238,0.6)"
                          : "1px solid rgba(255,255,255,0.08)",
                        borderRadius: 4,
                        color: "#e2e8f0",
                        fontSize: 10, fontWeight: 600,
                        cursor: widthLocked ? "not-allowed" : "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        minHeight: 22,
                      }}
                    >
                      <span style={{
                        display: "block",
                        height: Math.min(w.px, 5),
                        width: "70%",
                        background: "#e2e8f0",
                        borderRadius: 2,
                      }} />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── B7b+ — Apply style to all of same type ── */}
            {(() => {
              // Count of drawings of the same type (excluding locked and
              // the source). When 0, the bulk action would be a no-op so
              // we hide the row entirely to avoid a dead button.
              const srcType = ctxMenuTarget.type;
              const candidates = drawings.filter(
                (d) => d.type === srcType && d.id !== ctxMenuTarget.id && !d.locked,
              );
              if (candidates.length === 0) return null;
              const label = (tD.ctx_apply_to_type || "Aplicar a todos los del mismo type")
                .replace("{n}", candidates.length);
              return (
                <button
                  type="button"
                  onClick={ctxApplyStyleToType}
                  data-testid="liqmap-ctx-apply-to-type"
                  style={{
                    display: "block",
                    width: "100%",
                    padding: "7px 10px",
                    textAlign: "left",
                    background: "transparent",
                    border: "none",
                    borderTop: "1px solid rgba(255,255,255,0.06)",
                    marginTop: 2,
                    color: "#e2e8f0",
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: "pointer",
                    borderRadius: 5,
                    transition: "background 120ms",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(34,211,238,0.12)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                >
                  {label}
                </button>
              );
            })()}

            {/* Delete (separated as the destructive action) */}
            {items.slice(3).map((it) => (
              <button
                key={it.id}
                type="button"
                onClick={it.onClick}
                disabled={it.disabled}
                data-testid={it.testid}
                style={{
                  display: "block",
                  width: "100%",
                  padding: "7px 10px",
                  textAlign: "left",
                  background: "transparent",
                  border: "none",
                  borderTop: "1px solid rgba(255,255,255,0.06)",
                  marginTop: 2,
                  color: it.disabled ? "rgba(148,163,184,0.5)" : "#fca5a5",
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: it.disabled ? "not-allowed" : "pointer",
                  borderRadius: 5,
                  transition: "background 120ms",
                }}
                onMouseEnter={(e) => { if (!it.disabled) e.currentTarget.style.background = "rgba(239,68,68,0.12)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                {it.label}
              </button>
            ))}
          </div>
        );
      })()}

      <div
        className="absolute right-2 top-2 z-10 flex flex-col gap-1.5"
        data-testid="liqmap-zoom-controls"
        style={{ pointerEvents: "auto" }}
      >
        {[
          { sym: "+", testid: "liqmap-zoom-in", onClick: () => zoomBy(1.25, 0), title: "Zoom in" },
          { sym: "−", testid: "liqmap-zoom-out", onClick: () => zoomBy(1 / 1.25, 0), title: "Zoom out" },
          { sym: "↺", testid: "liqmap-zoom-reset", onClick: resetZoom, title: "Reset zoom" },
          {
            sym: isFullscreen ? "✕" : "⛶",
            testid: "liqmap-fullscreen",
            onClick: handleFullscreen,
            title: isFullscreen ? "Salir de pantalla completa" : "Pantalla completa",
          },
        ].map((b) => (
          <button
            key={b.testid}
            type="button"
            onClick={b.onClick}
            data-testid={b.testid}
            title={b.title}
            className="flex h-7 w-7 items-center justify-center font-bold transition-colors"
            style={{
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(249,115,22,0.3)",
              color: "#f97316",
              borderRadius: 6,
              fontSize: 14,
            }}
          >
            {b.sym}
          </button>
        ))}
      </div>
    </div>
  );
}
